// ??????????????????????????????????????????????????????????????????????????????�?// Inventory (stocktake) task �?helper submit / resubmit
// ??????????????????????????????????????????????????????????????????????????????�?
import { useState, useEffect, useCallback, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  APPROVAL_STATUS,
  COMMENT_TARGET_TYPE,
  INVENTORY_LEVEL,
  INVENTORY_OPEN_EXPIRING_QUERY_PARAM,
  LEVEL1_DEFAULTS,
  LIMIT_ERROR_CODE,
  TASK_KIND,
  USER_ROLE,
  getTodayDate,
  resolveInventoryExpiringSoonWindowDays,
  shouldSurfaceInventoryExpiryForSummary,
  type InventoryLevel,
  type TaskInstanceWithSubmission,
  type UserRole,
} from '@fdh/shared';

const shouldHelperOpenInventoryReadOnlyDetail = (
  data: TaskInstanceWithSubmission,
  role: UserRole | undefined,
): boolean => {
  if (role !== USER_ROLE.HELPER) {
    return false;
  }
  if (data.status === APPROVAL_STATUS.APPROVED || data.status === APPROVAL_STATUS.CANCEL) {
    return true;
  }
  if (data.status !== APPROVAL_STATUS.PENDING) {
    return false;
  }
  const submittedAt =
    data.submission?.submittedAt?.trim() ?? data.submittedAt?.trim() ?? '';
  return submittedAt !== '';
};
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import {
  CommentThread,
  UnsentCommentOnSubmitDialog,
  type CommentThreadHandle,
} from '../../components/comments';
import { ApiClientError, inventoryApi, taskInstanceApi } from '../../lib/api';
import { emitDailyQuotaLimitIfInsufficient, isLimitReachedError } from '../../lib/limitError';
import { navigateBack } from '../../lib/navigateBack';
import {
  buildHelperHomeStockOverviewPath,
  type HelperHomeStockOverviewLocationState,
} from '../../lib/helperHomeStockOverview';
import { getHelperTodayYmd, resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { isWithinHelperEarlySubmitRange } from '../../utils/taskEarlySubmit';
import { getFutureDueBannerMessage } from '../../utils/taskRelativeDueBanner';
import { TaskSubmitConfirmModal } from '../../components/task';
import { mapApiClientErrorToMessage } from '../../i18n/mapApiClientError';
import { CalendarIcon } from '../../components/CalendarIcon';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { NativeTimeInput } from '../../components/NativeTimeInput';
import {
  compressImageFile,
  resolveClientImageMime,
  validateReceiptFile,
  type CompressedReceiptBlob,
} from '../../lib/receiptImagePreprocess';
import {
  analyzeSelectedStocktakePhotos,
  uploadInventoryStocktake,
  InventoryStocktakeHttpError,
  type StocktakeAnalyzeTarget,
} from '../../lib/inventoryStocktakeAnalyzeApi';
import {
  InventoryStocktakeAiSelectModal,
  InventoryDeleteStocktakePhotoDialog,
  InventoryReanalyzePhotoConfirmDialog,
} from '../../components/inventory/InventoryStocktakePhotoModals';
import { StocktakeUploadedPhotoThumb } from '../../components/inventory/StocktakeUploadedPhotoThumb';
import { InventoryStocktakePhotoRadioPicker } from '../../components/inventory/InventoryStocktakePhotoRadioPicker';
import {
  applyFirstPhotoFallbackToLines,
  collectOrphanManualRowIdsForLocation,
  countLinesLinkedToCanonical,
  purgeLineDataForCanonicalPhoto,
  removeCategoriesFromNoItemsList,
  resolveLocationHubStatusLineKey,
} from '../../lib/inventoryTaskStocktakeHelpers';
import { settingsApi } from '../../lib/api';
import { resolvePairingScopedImageDisplayPath } from '../../lib/pairingImageStream';
import {
  buildInventoryPersistedPhotoApiPath,
  resolveInventoryPersistedPhotoAuthedApiPath,
  type InventoryPersistedPhotoRef,
} from '../../lib/inventoryPersistedPhotoApi';
import { InventorySummaryStatCardChevron } from '../../components/inventory/InventorySummaryStatCardChevron';
import { InventorySummaryStatDetailModal } from '../../components/inventory/InventorySummaryStatDetailModal';
import { HomeStockLocationPreviewModal } from '../../components/inventory/HomeStockLocationPreviewModal';
import { type HomeStockLocationResultItem } from '../../components/inventory/HomeStockLocationResultsSection';
import { HomeStockPreviewDocumentIcon } from '../../components/inventory/HomeStockPreviewDocumentIcon';
import { inventoryStockLevelUiLabel } from '../../lib/inventoryStockLevelUiLabel';
import { AppButton } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ModalCircleCloseButton } from '../../components/ui/ModalCircleCloseButton';
import {
  MODAL_BACKDROP_CENTER_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../lib/modalClasses';
import { buildInventoryV2CategoryBadgeIndexMap } from '../../lib/inventoryCategoryBadge';
import { InventoryCategoryBadge } from '../../components/inventory/InventoryCategoryBadge';
import { PressableHint } from '../../components/ui/PressableHint';
import { PressableHintProvider } from '../../components/ui/PressableHintContext';
import {
  AutoTranslationHint,
  resolveInstanceDisplayNeedsUpdateReason,
  resolveTaskDisplayDescription,
  resolveTaskDisplayTitle,
  resolveTaskWizardLocationDisplay,
  resolveTaskWizardCategoryDisplay,
  resolveTranslationTargetLanguageLabel,
  taskInstanceHasHelperTranslations,
} from '../../components/translation';

const getNowDatetimeInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  const h = parts.find((p) => p.type === 'hour')?.value ?? '';
  const min = parts.find((p) => p.type === 'minute')?.value ?? '';
  return `${y}-${m}-${d} ${h}:${min}`;
};

const splitDatetime = (value: string): { date: string; time: string } => {
  const [datePart = '', timePart = ''] = value.split(' ');
  return { date: datePart, time: timePart };
};

type LineState = {
  itemName: string;
  sortOrder: number;
  /** `null` until the helper or AI sets a level (no default). */
  status: InventoryLevel | null;
  existingPhotoUrl: string;
  everMatchedByAi: boolean;
  aiRaw: Record<string, unknown>;
  /** `true` �?NO_EXPIRY; `false` �?dated expiries; `null` �?helper must choose. */
  useNoExpiry: boolean | null;
  /** YYYY-MM-DD entries when `useNoExpiry` is false; duplicates removed on submit. */
  expiryYmdList: string[];
  itemNameEmployerDisplay?: string;
  itemNameHelperSnapshot?: string;
};

const EXPIRY_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const lineStateToPreviewExpiries = (
  st: LineState,
): Array<{ expiryDate: string; isNoExpiry?: boolean }> => {
  if (st.useNoExpiry === true) {
    return [{ expiryDate: 'NO_EXPIRY', isNoExpiry: true }];
  }
  if (st.useNoExpiry === false) {
    return [
      ...new Set(
        (st.expiryYmdList ?? []).map((d) => d.trim()).filter((d) => EXPIRY_YMD_REGEX.test(d)),
      ),
    ].map((expiryDate) => ({ expiryDate }));
  }
  return [];
};

const orderEmptyCategoriesForPreview = (
  noItemCategories: string[],
  wizardCats: string[],
): string[] => {
  const noSet = new Set(noItemCategories.map((c) => c.trim()).filter((c) => c !== ''));
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const c of wizardCats) {
    const trimmed = c.trim();
    if (trimmed !== '' && noSet.has(trimmed) && !seen.has(trimmed)) {
      ordered.push(trimmed);
      seen.add(trimmed);
    }
  }
  const rest = [...noSet].filter((c) => !seen.has(c)).sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
};

type StocktakePhotoLinkRef = {
  id: string;
  canonicalUrl: string;
  displayUrl: string;
  localPreviewUrl?: string;
  persistedPhotoRef?: InventoryPersistedPhotoRef;
};

const findStocktakePhotoByCanonical = (
  photos: StocktakePhotoLinkRef[],
  canonicalUrl: string,
): StocktakePhotoLinkRef | undefined => {
  const trimmed = canonicalUrl.trim();
  if (trimmed === '') {
    return undefined;
  }
  return photos.find((p) => p.canonicalUrl.trim() === trimmed);
};

const resolveStocktakePhotoLinkPreviewSrc = (photo: StocktakePhotoLinkRef): string => {
  const local = photo.localPreviewUrl?.trim() ?? '';
  if (local !== '') {
    return local;
  }
  return photo.displayUrl.trim();
};

const resolveStocktakeItemPreviewSrc = (item: StocktakeUploadedItem): string => {
  const local = item.localPreviewUrl?.trim() ?? '';
  if (local !== '') {
    return local;
  }
  return item.displayUrl.trim();
};

const revokeStocktakeLocalPreviewIfUnused = (
  remainingPhotos: StocktakeUploadedItem[],
  localPreviewUrl: string | undefined,
): void => {
  const url = localPreviewUrl?.trim();
  if (!url || !url.startsWith('blob:')) {
    return;
  }
  const stillUsed = remainingPhotos.some((photo) => photo.localPreviewUrl?.trim() === url);
  if (!stillUsed) {
    URL.revokeObjectURL(url);
  }
};

const resolveLinePhotoDisplayUrl = (photos: StocktakePhotoLinkRef[], existingPhotoUrl: string): string => {
  const linked = findStocktakePhotoByCanonical(photos, existingPhotoUrl);
  if (linked) {
    return resolveStocktakePhotoLinkPreviewSrc(linked);
  }
  const first = photos[0];
  if (first) {
    return resolveStocktakePhotoLinkPreviewSrc(first);
  }
  return resolvePairingScopedImageDisplayPath(existingPhotoUrl);
};

const resolveLineSelectedPhotoId = (photos: StocktakePhotoLinkRef[], existingPhotoUrl: string): string => {
  const linked = findStocktakePhotoByCanonical(photos, existingPhotoUrl);
  if (linked) {
    return linked.id;
  }
  if (photos.length === 1) {
    return photos[0]?.id ?? '';
  }
  return '';
};

const maxExpiryDatesPerItem = 10;

const defaultLineExpiry = (): Pick<LineState, 'useNoExpiry' | 'expiryYmdList'> => ({
  useNoExpiry: null,
  expiryYmdList: [],
});

const hydrateExpiryFromSubmissionItem = (
  expiries: Array<{ expiryDate: string; isNoExpiry?: boolean }> | undefined,
): Pick<LineState, 'useNoExpiry' | 'expiryYmdList'> => {
  if (!expiries || expiries.length === 0) {
    return { useNoExpiry: null, expiryYmdList: [] };
  }
  const hasNoExpiry = expiries.some((e) => e.isNoExpiry === true || e.expiryDate === 'NO_EXPIRY');
  if (hasNoExpiry) {
    return { useNoExpiry: true, expiryYmdList: [] };
  }
  const dates = expiries
    .map((e) => e.expiryDate.trim())
    .filter((d) => EXPIRY_YMD_REGEX.test(d));
  if (dates.length === 0) {
    return { useNoExpiry: null, expiryYmdList: [] };
  }
  return { useNoExpiry: false, expiryYmdList: dates };
};

const buildExpiriesPayload = (st: LineState): Array<{ expiryDate: string }> => {
  if (st.useNoExpiry === true) {
    return [{ expiryDate: 'NO_EXPIRY' }];
  }
  if (st.useNoExpiry === false) {
    const dates = [
      ...new Set((st.expiryYmdList ?? []).map((d) => d.trim()).filter((d) => EXPIRY_YMD_REGEX.test(d))),
    ].sort();
    return dates.map((expiryDate) => ({ expiryDate }));
  }
  return [];
};

const LEVELS: InventoryLevel[] = [
  INVENTORY_LEVEL.FULL,
  INVENTORY_LEVEL.HALF,
  INVENTORY_LEVEL.LOW,
  INVENTORY_LEVEL.EMPTY,
];

const parseInventoryStatus = (raw: string): InventoryLevel | null => {
  const s = raw.trim().toLowerCase();
  return LEVELS.includes(s as InventoryLevel) ? (s as InventoryLevel) : null;
};

const maxStocktakeFiles = LEVEL1_DEFAULTS.MAX_RECEIPT_FILES_PER_REQUEST;

const maxManualInventoryTemplateRows = 30;

/** Screen flow for helper stocktake wizard (MainAppShell already provides top menu + bottom nav). */
type InventoryHelperScreen = 'photos' | 'itemEdit' | 'final';

const ymdToUtcNoonMs = (ymd: string): number => {
  const [yy = '0', mm = '01', dd = '01'] = ymd.split('-');
  return Date.UTC(Number(yy), Number(mm) - 1, Number(dd), 12, 0, 0, 0);
};

const diffCalendarDaysYmd = (fromYmd: string, toYmd: string): number =>
  Math.round((ymdToUtcNoonMs(toYmd) - ymdToUtcNoonMs(fromYmd)) / 86400000);

const newStocktakeUploadId = (): string =>
  typeof globalThis !== 'undefined' &&
  globalThis.crypto &&
  typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `st-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

type StocktakeUploadedItem = {
  id: string;
  displayUrl: string;
  /** Canonical GCS / stored URL; used to clear `lineMap` rows when removing. */
  canonicalUrl: string;
  aiAnalysisCompleted: boolean;
  /** Local blob URL from the picked file; preview before save / resubmit reload. */
  localPreviewUrl?: string;
  /** Set when hydrated from an existing submission (resubmit / reload). */
  persistedPhotoRef?: InventoryPersistedPhotoRef;
};

/** Load persisted location batch photos: canonical URLs for submit; opaque API paths for display. */
const mapSubmissionLocationPhotoUrlsToStocktakeItems = (params: {
  locationKeyPrefix: string;
  locationIndex: number;
  photoUrls: string[];
  photoUrlsForDisplay?: string[];
  locationPhotos?: Array<{ url: string; aiAnalysisCompleted: boolean }>;
}): StocktakeUploadedItem[] => {
  const entries =
    params.locationPhotos && params.locationPhotos.length > 0
      ? params.locationPhotos
      : params.photoUrls
          .filter((u): u is string => typeof u === 'string')
          .map((u) => u.trim())
          .filter((u) => u !== '')
          .map((url) => ({ url, aiAnalysisCompleted: true }));
  return entries.map((entry, photoIndex) => {
    const canonical = entry.url.trim();
    const displayRaw = params.photoUrlsForDisplay?.[photoIndex]?.trim() ?? '';
    const display =
      displayRaw !== ''
        ? resolvePairingScopedImageDisplayPath(displayRaw)
        : resolvePairingScopedImageDisplayPath(canonical);
    return {
      id: `loaded-${params.locationKeyPrefix}-${photoIndex}-${canonical.slice(0, 36)}`,
      displayUrl: display,
      canonicalUrl: canonical,
      aiAnalysisCompleted: Boolean(entry.aiAnalysisCompleted),
      persistedPhotoRef: {
        variant: 'v2Location',
        locationIndex: params.locationIndex,
        photoIndex,
      },
    };
  });
};

const resolveEditableTemplateRows = (
  taskInstance: TaskInstanceWithSubmission | null
): { itemId: string; itemName: string; sortOrder: number }[] => {
  if (!taskInstance) {
    return [];
  }
  const isResubmit = taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE;
  const submittedLines = taskInstance.inventorySubmission?.lines ?? [];
  if (isResubmit && submittedLines.length > 0) {
    return submittedLines.map((line) => ({
      itemId: line.itemId,
      itemName: line.itemName,
      sortOrder: line.sortOrder,
    }));
  }
  return taskInstance.inventoryTemplate ?? [];
};

/** Batch photo URL for submission: prefer a line's own URL, else any uploaded stocktake image. */
const resolveStocktakeSharedPhotoUrl = (params: {
  rowsForPhotoFallback: { itemId: string }[];
  lineMap: Record<string, LineState>;
  uploadedStocktakeItems: StocktakeUploadedItem[];
}): string => {
  const fromLine = params.rowsForPhotoFallback
    .map((row) => params.lineMap[row.itemId]?.existingPhotoUrl?.trim() ?? '')
    .find((u) => u.length > 0);
  if (fromLine) {
    return fromLine;
  }
  const fromUpload = params.uploadedStocktakeItems.find((p) => p.canonicalUrl.trim() !== '');
  return fromUpload?.canonicalUrl.trim() ?? '';
};

type InvSubmissionV2Loc = NonNullable<
  NonNullable<TaskInstanceWithSubmission['inventorySubmission']>['locations']
>[number];

type ManualInventoryTemplateRowMeta = {
  itemId: string;
  itemName: string;
  categoryName: string;
  sortOrder: number;
  /** Multi-location wizard: row was captured / added only for this employer location label */
  introducedLocation?: string;
};

const filterStocktakeRowsForWizardLocation = (
  rows: { itemId: string; itemName: string; sortOrder: number }[],
  manualRows: ManualInventoryTemplateRowMeta[],
  manualIds: Set<string>,
  wizardLocs: string[],
  locationName: string,
): typeof rows =>
  rows.filter((r) => {
    if (!manualIds.has(r.itemId)) {
      return true;
    }
    const intro = manualRows.find((m) => m.itemId === r.itemId)?.introducedLocation?.trim() ?? '';
    if (wizardLocs.length <= 1) {
      return true;
    }
    if (intro !== '') {
      return intro === locationName;
    }
    return (wizardLocs[0] ?? '') === locationName;
  });

const buildManualRowsFromSubmissionV2 = (
  invLocations: InvSubmissionV2Loc[] | undefined,
  wizardLocationNames: string[],
): {
  manualRows: ManualInventoryTemplateRowMeta[];
  pairLocKeyToId: Map<string, string>;
} => {
  const pairLocKeyToId = new Map<string, string>();
  const manualRows: ManualInventoryTemplateRowMeta[] = [];
  const newLocalId = (): string =>
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `mv2-${manualRows.length}-${Date.now().toString(36)}`;

  invLocations?.forEach((locData) => {
    const ln = locData.locationName;
    if (wizardLocationNames.length > 0 && !wizardLocationNames.includes(locData.locationName)) {
      return;
    }
    locData.items.forEach((item) => {
      const nk = `${item.itemName.trim().toLowerCase()}|||${item.categoryName.trim().toLowerCase()}`;
      const lk = `${ln}|||${nk}`;
      if (pairLocKeyToId.has(lk)) {
        return;
      }
      const id = newLocalId();
      pairLocKeyToId.set(lk, id);
      manualRows.push({
        itemId: id,
        itemName: item.itemName.trim(),
        categoryName: item.categoryName.trim(),
        sortOrder: manualRows.length,
        introducedLocation: ln,
      });
    });
  });
  return { manualRows, pairLocKeyToId };
};

export const InventoryTaskPage = () => {
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: instanceId } = useParams<{ id: string }>();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [taskInstance, setTaskInstance] = useState<TaskInstanceWithSubmission | null>(null);
  const [lineMap, setLineMap] = useState<Record<string, LineState>>({});
  const [completedDate, setCompletedDate] = useState('');
  const [completedTime, setCompletedTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkBusyMode, setBulkBusyMode] = useState<'upload' | 'analyze' | null>(null);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [pageRateInfo, setPageRateInfo] = useState<string | null>(null);
  const [uploadedStocktakeItems, setUploadedStocktakeItems] = useState<StocktakeUploadedItem[]>([]);
  const [removeConfirmItem, setRemoveConfirmItem] = useState<StocktakeUploadedItem | null>(null);
  const [aiPhotoSelectModalOpen, setAiPhotoSelectModalOpen] = useState(false);
  const [reanalyzeConfirmOpen, setReanalyzeConfirmOpen] = useState(false);
  const [pendingAiPhotoIds, setPendingAiPhotoIds] = useState<string[]>([]);
  const [manualAddPhotoId, setManualAddPhotoId] = useState('');
  const pendingUploadBlobsRef = useRef<Map<string, CompressedReceiptBlob>>(new Map());
  const [removeManualItemId, setRemoveManualItemId] = useState<string | null>(null);
  const [renameModalItemId, setRenameModalItemId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [categoryEditingItemId, setCategoryEditingItemId] = useState<string | null>(null);
  const [unsentCommentModalOpen, setUnsentCommentModalOpen] = useState(false);
  const [submitConfirmModalOpen, setSubmitConfirmModalOpen] = useState(false);
  const [unsentCommentDialogBusy, setUnsentCommentDialogBusy] = useState(false);
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);
  const resolveWizardLocationLabel = useCallback(
    (locationName: string): string => {
      const trimmed = locationName.trim();
      if (!taskInstance?.task || trimmed === '') {
        return locationName;
      }
      return resolveTaskWizardLocationDisplay(
        taskInstance.task,
        locationName,
        showTaskContentOriginal,
      );
    },
    [showTaskContentOriginal, taskInstance],
  );
  const resolveWizardCategoryLabel = useCallback(
    (categoryName: string): string => {
      const trimmed = categoryName.trim();
      if (!taskInstance?.task || trimmed === '') {
        return categoryName;
      }
      return resolveTaskWizardCategoryDisplay(
        taskInstance.task,
        categoryName,
        showTaskContentOriginal,
      );
    },
    [showTaskContentOriginal, taskInstance],
  );
  const [wizardCategories, setWizardCategories] = useState<string[]>([]);
  const [wizardLocations, setWizardLocations] = useState<string[]>([]);
  const [helperScreen, setHelperScreen] = useState<InventoryHelperScreen>('photos');
  const [selectLocationModalOpen, setSelectLocationModalOpen] = useState(false);
  const [selectLocationModalCoachmarkOpen, setSelectLocationModalCoachmarkOpen] = useState(false);
  const [leaveHomeStockWizardConfirmOpen, setLeaveHomeStockWizardConfirmOpen] = useState(false);
  const [locationStepIndex, setLocationStepIndex] = useState(0);
  const [lineMapsByLocation, setLineMapsByLocation] = useState<Record<string, Record<string, LineState>>>(
    {},
  );
  const [photosByLocation, setPhotosByLocation] = useState<Record<string, StocktakeUploadedItem[]>>({});
  const stocktakePhotosRef = useRef({
    uploadedStocktakeItems,
    photosByLocation,
  });
  stocktakePhotosRef.current = { uploadedStocktakeItems, photosByLocation };
  const [noItemsByLocation, setNoItemsByLocation] = useState<Record<string, string[]>>({});
  const [noItemsConfirmModalOpen, setNoItemsConfirmModalOpen] = useState(false);
  const [noItemsConfirmCategories, setNoItemsConfirmCategories] = useState<string[]>([]);
  const [noItemsConfirmChecked, setNoItemsConfirmChecked] = useState<Record<string, boolean>>({});
  const noItemsConfirmLocationRef = useRef<string>('');
  const resumeAfterNoItemsConfirmRef = useRef(false);
  const [fallbackTemplateRows, setFallbackTemplateRows] = useState<
    { itemId: string; itemName: string; sortOrder: number; itemCategory: string }[]
  >([]);
  /** Helper-entered rows for this submission (v2 uses itemName/categoryName; IDs are local or from `createItem`). */
  const [manualTemplateRows, setManualTemplateRows] = useState<ManualInventoryTemplateRowMeta[]>([]);
  const [manualAddItemName, setManualAddItemName] = useState('');
  const [manualAddCategorySelect, setManualAddCategorySelect] = useState('');
  const [manualAddCategoryOther, setManualAddCategoryOther] = useState('');
  const [manualAddBusy, setManualAddBusy] = useState(false);
  const [manualAddStatus, setManualAddStatus] = useState<InventoryLevel | null>(null);
  const [manualAddUseNoExpiry, setManualAddUseNoExpiry] = useState<boolean | null>(null);
  const [manualAddExpiryYmdList, setManualAddExpiryYmdList] = useState<string[]>([]);
  const [manualAddSectionExpanded, setManualAddSectionExpanded] = useState(false);
  const [finalReviewStatModalKind, setFinalReviewStatModalKind] = useState<
    null | 'lowStock' | 'expiring'
  >(null);
  const [finalReviewLocationPreview, setFinalReviewLocationPreview] = useState<string | null>(null);
  const [helperCanSaveItemsToMaster, setHelperCanSaveItemsToMaster] = useState(false);
  const wizardLocationsRef = useRef<string[]>([]);
  const locationStepIndexRef = useRef(0);
  const lineMapsByLocationRef = useRef<Record<string, Record<string, LineState>>>({});
  const lineMapRef = useRef<Record<string, LineState>>({});
  const commentThreadRef = useRef<CommentThreadHandle | null>(null);
  const expiryPickerDateBounds = useMemo(() => {
    const todayYmd = getHelperTodayYmd(user?.settings?.timezone);
    const currentYear = Number(todayYmd.slice(0, 4));
    const safeCurrentYear = Number.isFinite(currentYear) ? currentYear : new Date().getFullYear();
    return {
      minDate: `${safeCurrentYear - 3}-01-01`,
      maxDate: `${safeCurrentYear + 20}-12-31`,
    };
  }, [user?.settings?.timezone]);

  const templateOrdered = useMemo(() => {
    const tpl = resolveEditableTemplateRows(taskInstance);
    const base: { itemId: string; itemName: string; sortOrder: number }[] =
      !tpl || tpl.length === 0
        ? fallbackTemplateRows.map((row) => ({
            itemId: row.itemId,
            itemName: row.itemName,
            sortOrder: row.sortOrder,
          }))
        : [...tpl].sort((a, b) => a.sortOrder - b.sortOrder || a.itemId.localeCompare(b.itemId));
    const manualSorted = [...manualTemplateRows]
      .map((row) => ({
        itemId: row.itemId,
        itemName: row.itemName,
        sortOrder: row.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.itemId.localeCompare(b.itemId));
    return [...base, ...manualSorted].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.itemId.localeCompare(b.itemId),
    );
  }, [fallbackTemplateRows, manualTemplateRows, taskInstance]);

  const manualItemIdSet = useMemo(
    () => new Set(manualTemplateRows.map((row) => row.itemId)),
    [manualTemplateRows],
  );

  const isListlessCategoryStocktake = useMemo(
    () => !taskInstance?.task.inventoryListId?.trim() && wizardCategories.length > 0,
    [taskInstance?.task.inventoryListId, wizardCategories.length],
  );

  const wizardCategoryBadgeIndexMap = useMemo(
    () => buildInventoryV2CategoryBadgeIndexMap(wizardCategories),
    [wizardCategories],
  );

  const stocktakeRowsOrdered = useMemo(() => {
    if (!isListlessCategoryStocktake) {
      return templateOrdered;
    }
    return templateOrdered.filter((r) => manualItemIdSet.has(r.itemId));
  }, [isListlessCategoryStocktake, manualItemIdSet, templateOrdered]);

  const activeLocationKeyForNoItems =
    wizardLocations.length > 1 ? wizardLocations[locationStepIndex] ?? '' : wizardLocations[0] ?? 'Default';

  const stocktakeRowsForActiveWizardUi = useMemo(() => {
    if (wizardLocations.length <= 1) {
      return stocktakeRowsOrdered;
    }
    return filterStocktakeRowsForWizardLocation(
      stocktakeRowsOrdered,
      manualTemplateRows,
      manualItemIdSet,
      wizardLocations,
      activeLocationKeyForNoItems,
    );
  }, [
    activeLocationKeyForNoItems,
    manualItemIdSet,
    manualTemplateRows,
    stocktakeRowsOrdered,
    wizardLocations,
  ]);

  const fallbackCategoryByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of fallbackTemplateRows) {
      const cat = row.itemCategory.trim();
      if (cat !== '') {
        map.set(row.itemId, cat);
      }
    }
    return map;
  }, [fallbackTemplateRows]);

  const lineCategoryByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const line of taskInstance?.inventorySubmission?.lines ?? []) {
      const category = line.itemCategory?.trim() ?? '';
      if (category === '') {
        continue;
      }
      map.set(line.itemId, category);
    }
    return map;
  }, [taskInstance]);

  const manualCategoryByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of manualTemplateRows) {
      const c = row.categoryName.trim();
      if (c !== '') {
        map.set(row.itemId, c);
      }
    }
    return map;
  }, [manualTemplateRows]);

  const getRowCategory = useCallback(
    (itemId: string): string => {
      const fromManual = manualCategoryByItemId.get(itemId)?.trim() ?? '';
      if (fromManual !== '') {
        return fromManual;
      }
      const fromLine = lineCategoryByItemId.get(itemId)?.trim() ?? '';
      if (fromLine !== '') {
        return fromLine;
      }
      const fromFallback = fallbackCategoryByItemId.get(itemId)?.trim() ?? '';
      if (fromFallback !== '') {
        return fromFallback;
      }
      if (wizardCategories.length === 1) {
        return wizardCategories[0] ?? 'Uncategorized';
      }
      return 'Uncategorized';
    },
    [fallbackCategoryByItemId, lineCategoryByItemId, manualCategoryByItemId, wizardCategories],
  );

  const clearNoItemsMarkForCategoryAtActiveLocation = useCallback(
    (category: string): void => {
      if (wizardCategories.length === 0) {
        return;
      }
      const trimmed = category.trim();
      const loc =
        wizardLocations.length > 1 ? wizardLocations[locationStepIndex] ?? '' : wizardLocations[0] ?? 'Default';
      if (loc === '' || trimmed === '') {
        return;
      }
      setNoItemsByLocation((prev) => {
        const cur = prev[loc] ?? [];
        const next = removeCategoriesFromNoItemsList(cur, [trimmed]);
        if (next.length === cur.length) {
          return prev;
        }
        return { ...prev, [loc]: next };
      });
    },
    [locationStepIndex, wizardCategories.length, wizardLocations],
  );

  const clearNoItemsMarkForItemCategory = useCallback(
    (itemId: string): void => {
      clearNoItemsMarkForCategoryAtActiveLocation(getRowCategory(itemId));
    },
    [clearNoItemsMarkForCategoryAtActiveLocation, getRowCategory],
  );

  const isMultiLocationWizard = wizardLocations.length > 1;
  const currentLocationName = isMultiLocationWizard ? wizardLocations[locationStepIndex] ?? '' : '';

  const activeLineMap = useMemo(() => {
    if (isMultiLocationWizard) {
      return lineMapsByLocation[currentLocationName] ?? {};
    }
    return lineMap;
  }, [isMultiLocationWizard, lineMapsByLocation, currentLocationName, lineMap]);

  const activeUploadedStocktakeItems = useMemo(() => {
    if (isMultiLocationWizard) {
      return photosByLocation[currentLocationName] ?? [];
    }
    return uploadedStocktakeItems;
  }, [isMultiLocationWizard, photosByLocation, currentLocationName, uploadedStocktakeItems]);

  const hasActiveStocktakePhotos = useMemo(
    () => activeUploadedStocktakeItems.some((p) => p.canonicalUrl.trim() !== ''),
    [activeUploadedStocktakeItems],
  );

  /** Item rows, manual add, and AI �unrecognized�?list only on the review screen after photos exist. */
  const showInventoryLineCards = useMemo(() => {
    if (helperScreen !== 'itemEdit') {
      return false;
    }
    if (isMultiLocationWizard) {
      return hasActiveStocktakePhotos;
    }
    const hasPhoto = uploadedStocktakeItems.some((p) => p.canonicalUrl.trim() !== '');
    return hasPhoto;
  }, [hasActiveStocktakePhotos, helperScreen, isMultiLocationWizard, uploadedStocktakeItems]);

  const totalMajorWizardSteps =
    wizardLocations.length > 0
      ? isMultiLocationWizard
        ? wizardLocations.length + 1
        : 2
      : 2;
  const majorStepDisplay =
    helperScreen === 'final'
      ? totalMajorWizardSteps
      : isMultiLocationWizard
        ? locationStepIndex + 1
        : 1;
  const stepSubSuffix =
    helperScreen === 'final' ? '' : helperScreen === 'photos' ? 'A' : 'B';
  const progressFractionWithinMajors = (() => {
    if (helperScreen === 'final') {
      return 1;
    }
    const base = majorStepDisplay - 1;
    const half = helperScreen === 'photos' ? 0.25 : 0.75;
    return (base + half) / totalMajorWizardSteps;
  })();
  const helperFlowProgressPercent = Math.round(100 * progressFractionWithinMajors);

  const notRecognizedByAi = useMemo(() => {
    if (isListlessCategoryStocktake) {
      return [];
    }
    if (!hasActiveStocktakePhotos) {
      return [];
    }
    return stocktakeRowsForActiveWizardUi.filter((row) => {
      const st = activeLineMap[row.itemId];
      return st && !st.everMatchedByAi;
    });
  }, [
    activeLineMap,
    hasActiveStocktakePhotos,
    isListlessCategoryStocktake,
    stocktakeRowsForActiveWizardUi,
  ]);

  useEffect(() => {
    wizardLocationsRef.current = wizardLocations;
  }, [wizardLocations]);

  useEffect(() => {
    locationStepIndexRef.current = locationStepIndex;
  }, [locationStepIndex]);

  useEffect(() => {
    lineMapsByLocationRef.current = lineMapsByLocation;
  }, [lineMapsByLocation]);

  useEffect(() => {
    lineMapRef.current = lineMap;
  }, [lineMap]);

  useEffect(() => {
    return () => {
      const seen = new Set<string>();
      for (const photo of stocktakePhotosRef.current.uploadedStocktakeItems) {
        const url = photo.localPreviewUrl?.trim();
        if (url && url.startsWith('blob:') && !seen.has(url)) {
          seen.add(url);
          URL.revokeObjectURL(url);
        }
      }
      for (const photos of Object.values(stocktakePhotosRef.current.photosByLocation)) {
        for (const photo of photos) {
          const url = photo.localPreviewUrl?.trim();
          if (url && url.startsWith('blob:') && !seen.has(url)) {
            seen.add(url);
            URL.revokeObjectURL(url);
          }
        }
      }
    };
  }, []);

  // If photos were uploaded before wizard locations finished loading, they may sit in uploadedStocktakeItems.
  useEffect(() => {
    if (loading || !isMultiLocationWizard || uploadedStocktakeItems.length === 0) {
      return;
    }
    const loc = wizardLocations[locationStepIndex] ?? wizardLocations[0] ?? '';
    if (loc === '') {
      return;
    }
    setPhotosByLocation((prev) => {
      const cur = [...(prev[loc] ?? [])];
      const seen = new Set(cur.map((p) => p.canonicalUrl.trim()).filter((u) => u !== ''));
      for (const photo of uploadedStocktakeItems) {
        const canonical = photo.canonicalUrl.trim();
        if (canonical === '' || seen.has(canonical)) {
          continue;
        }
        seen.add(canonical);
        cur.push(photo);
      }
      if (cur.length === (prev[loc] ?? []).length) {
        return prev;
      }
      return { ...prev, [loc]: cur };
    });
    setUploadedStocktakeItems([]);
  }, [isMultiLocationWizard, loading, locationStepIndex, uploadedStocktakeItems, wizardLocations]);

  useEffect(() => {
    if (!selectLocationModalOpen || !instanceId || !isMultiLocationWizard) {
      return;
    }
    const key = `fdh-inv-helper-loc-coach-${instanceId}`;
    setSelectLocationModalCoachmarkOpen(sessionStorage.getItem(key) !== '1');
  }, [selectLocationModalOpen, instanceId, isMultiLocationWizard]);

  const fetchInstance = useCallback(async () => {
    if (!instanceId) return;
    setLoading(true);
    setError(null);
    setSelectLocationModalOpen(false);
    setManualTemplateRows([]);
    setManualAddItemName('');
    setManualAddCategorySelect('');
    setManualAddCategoryOther('');
    setManualAddSectionExpanded(false);
    try {
      const inventorySettingsPromise = inventoryApi.getSettings().catch(() => ({
        success: false as const,
      }));
      const response = await taskInstanceApi.get(instanceId, language);
      if (!response.success || !response.data) {
        setError(response.error ?? t('task.review.loadError'));
        return;
      }
      const data = response.data;
      if (data.task.taskKind !== TASK_KIND.INVENTORY) {
        navigate(`/tasks/complete/${instanceId}`, { replace: true });
        return;
      }

      if (shouldHelperOpenInventoryReadOnlyDetail(data, user?.role)) {
        navigate(`/task-instances/${instanceId}`, { replace: true });
        return;
      }

      const invSettingsRaw = await inventorySettingsPromise;
      setHelperCanSaveItemsToMaster(
        invSettingsRaw.success === true && Boolean(invSettingsRaw.data?.helperCanEditItems),
      );

      setTaskInstance(data);

      const inv = data.inventorySubmission;
      const editableTemplateRows = resolveEditableTemplateRows(data);
      let effectiveTemplateRows = editableTemplateRows;
      if (editableTemplateRows.length === 0) {
        if (!data.task.inventoryListId?.trim()) {
          effectiveTemplateRows = [];
          setFallbackTemplateRows([]);
        } else {
          try {
            const listItemsRes = await inventoryApi.listItems();
            if (listItemsRes.success && listItemsRes.data) {
              const fallbackRows = listItemsRes.data.map((item, idx) => ({
                itemId: item.id,
                itemName: item.name,
                sortOrder: idx,
                itemCategory: item.category ?? '',
              }));
              effectiveTemplateRows = fallbackRows;
              setFallbackTemplateRows(fallbackRows);
            } else {
              setFallbackTemplateRows([]);
            }
          } catch {
            setFallbackTemplateRows([]);
          }
        }
      } else {
        setFallbackTemplateRows([]);
      }

      const categoryByItemIdFromSubmission = new Map<string, string>();
      for (const line of inv?.lines ?? []) {
        const c = line.itemCategory?.trim() ?? '';
        if (c !== '') {
          categoryByItemIdFromSubmission.set(line.itemId, c);
        }
      }

      const catLabelForRow = (itemId: string): string => {
        const fromSub = categoryByItemIdFromSubmission.get(itemId)?.trim() ?? '';
        if (fromSub !== '') {
          return fromSub;
        }
        return 'Uncategorized';
      };

      let locationsFromApi: string[] = [];
      let categoriesFromApi: string[] = [];
      try {
        const wizardRes = await inventoryApi.getTaskInstanceWizard(instanceId);
        if (wizardRes.success && wizardRes.data) {
          locationsFromApi = Array.isArray(wizardRes.data.locations)
            ? wizardRes.data.locations.filter((value): value is string => typeof value === 'string')
            : [];
          categoriesFromApi = Array.isArray(wizardRes.data.categories)
            ? wizardRes.data.categories.filter((value): value is string => typeof value === 'string')
            : [];
        }
      } catch {
        locationsFromApi = [];
        categoriesFromApi = [];
      }
      const wizardCatsEffective =
        categoriesFromApi.length > 0
          ? categoriesFromApi
          : (data.task.inventoryCategories ?? []).filter((c): c is string => typeof c === 'string');
      setWizardLocations(locationsFromApi);
      setWizardCategories(wizardCatsEffective);

      const listlessLoad = !data.task.inventoryListId?.trim() && wizardCatsEffective.length > 0;

      const isMulti = locationsFromApi.length > 1;
      setLocationStepIndex(0);

      if (isMulti) {
        if (listlessLoad) {
          const { manualRows, pairLocKeyToId } = buildManualRowsFromSubmissionV2(inv?.locations, locationsFromApi);
          setManualTemplateRows(manualRows);

          const initialLineMaps: Record<string, Record<string, LineState>> = {};
          const initialPhotos: Record<string, StocktakeUploadedItem[]> = {};
          const initialNoItems: Record<string, string[]> = {};
          for (const locName of locationsFromApi) {
            initialLineMaps[locName] = {};
            initialPhotos[locName] = [];
            initialNoItems[locName] = [];
          }

          inv?.locations?.forEach((locData) => {
            const ln = locData.locationName;
            if (!initialLineMaps[ln]) {
              return;
            }
            initialNoItems[ln] = [...locData.noItemsCategories];
            initialPhotos[ln] = mapSubmissionLocationPhotoUrlsToStocktakeItems({
              locationKeyPrefix: ln,
              locationIndex: locationsFromApi.indexOf(ln),
              photoUrls: locData.photoUrls,
              photoUrlsForDisplay: locData.photoUrlsForDisplay,
              locationPhotos: locData.locationPhotos,
            });
            for (const item of locData.items) {
              const nk = `${item.itemName.trim().toLowerCase()}|||${item.categoryName.trim().toLowerCase()}`;
              const lk = `${ln}|||${nk}`;
              const idKey = pairLocKeyToId.get(lk);
              if (!idKey) {
                continue;
              }
              const ar =
                item.aiRaw && typeof item.aiRaw === 'object'
                  ? (item.aiRaw as Record<string, unknown>)
                  : {};
              const fromAi = ar.stocktake === true;
              initialLineMaps[ln][idKey] = {
                itemName: item.itemName.trim(),
                sortOrder: item.sortOrder,
                status: item.stockLevel,
                existingPhotoUrl: item.photoUrl ?? '',
                everMatchedByAi: fromAi,
                aiRaw: { ...ar },
                ...hydrateExpiryFromSubmissionItem(item.expiries),
              };
            }
          });

          for (const locName of locationsFromApi) {
            for (const row of manualRows) {
              if (row.introducedLocation !== locName) {
                continue;
              }
              if (!initialLineMaps[locName][row.itemId]) {
                initialLineMaps[locName][row.itemId] = {
                  itemName: row.itemName,
                  sortOrder: row.sortOrder,
                  status: null,
                  existingPhotoUrl: '',
                  everMatchedByAi: false,
                  aiRaw: {},
                  ...defaultLineExpiry(),
                };
              }
            }
          }

          setLineMapsByLocation(initialLineMaps);
          setPhotosByLocation(initialPhotos);
          setNoItemsByLocation(initialNoItems);
          setLineMap({});
          setUploadedStocktakeItems([]);
          {
            const firstLoc = locationsFromApi[0] ?? '';
            const ph0 = initialPhotos[firstLoc] ?? [];
            const hasPhoto0 = ph0.some((p) => p.canonicalUrl.trim() !== '');
            const openPhotosFirst =
              data.status === APPROVAL_STATUS.NEEDS_UPDATE || !hasPhoto0;
            setHelperScreen(openPhotosFirst ? 'photos' : 'itemEdit');
            setSelectLocationModalOpen(true);
          }
        } else {
        const initialLineMaps: Record<string, Record<string, LineState>> = {};
        const initialPhotos: Record<string, StocktakeUploadedItem[]> = {};
        const initialNoItems: Record<string, string[]> = {};
        for (const locName of locationsFromApi) {
          initialLineMaps[locName] = {};
          initialPhotos[locName] = [];
          initialNoItems[locName] = [];
        }

        const invLocations = inv?.locations;
        if (invLocations && invLocations.length > 0) {
          for (const locData of invLocations) {
            const ln = locData.locationName;
            if (!initialLineMaps[ln]) {
              continue;
            }
            initialNoItems[ln] = [...locData.noItemsCategories];
            initialPhotos[ln] = mapSubmissionLocationPhotoUrlsToStocktakeItems({
              locationKeyPrefix: ln,
              locationIndex: locationsFromApi.indexOf(ln),
              photoUrls: locData.photoUrls,
              photoUrlsForDisplay: locData.photoUrlsForDisplay,
              locationPhotos: locData.locationPhotos,
            });
            for (const item of locData.items) {
              const rowMatch =
                effectiveTemplateRows.find((r) => r.itemName === item.itemName && catLabelForRow(r.itemId) === item.categoryName) ??
                effectiveTemplateRows.find((r) => r.itemName === item.itemName);
              if (!rowMatch) {
                continue;
              }
              const ar =
                item.aiRaw && typeof item.aiRaw === 'object' ? (item.aiRaw as Record<string, unknown>) : {};
              const fromAi = ar.stocktake === true;
              initialLineMaps[ln][rowMatch.itemId] = {
                itemName: item.itemName,
                sortOrder: item.sortOrder,
                status: item.stockLevel,
                existingPhotoUrl: item.photoUrl ?? '',
                everMatchedByAi: fromAi,
                aiRaw: { ...ar },
                ...hydrateExpiryFromSubmissionItem(item.expiries),
              };
            }
          }
        }

        for (const locName of locationsFromApi) {
          for (const row of effectiveTemplateRows) {
            if (!initialLineMaps[locName][row.itemId]) {
              initialLineMaps[locName][row.itemId] = {
                itemName: row.itemName,
                sortOrder: row.sortOrder,
                status: null,
                existingPhotoUrl: '',
                everMatchedByAi: false,
                aiRaw: {},
                ...defaultLineExpiry(),
              };
            }
          }
        }

        setLineMapsByLocation(initialLineMaps);
        setPhotosByLocation(initialPhotos);
        setNoItemsByLocation(initialNoItems);
        setLineMap({});
        setUploadedStocktakeItems([]);
        {
          const firstLoc = locationsFromApi[0] ?? '';
          const ph0 = initialPhotos[firstLoc] ?? [];
          const hasPhoto0 = ph0.some((p) => p.canonicalUrl.trim() !== '');
          const openPhotosFirst = data.status === APPROVAL_STATUS.NEEDS_UPDATE || !hasPhoto0;
          setHelperScreen(openPhotosFirst ? 'photos' : 'itemEdit');
          setSelectLocationModalOpen(true);
        }
        }
      } else if (listlessLoad) {
        const { manualRows, pairLocKeyToId } = buildManualRowsFromSubmissionV2(inv?.locations, locationsFromApi);
        setManualTemplateRows(manualRows);

        setLineMapsByLocation({});
        setPhotosByLocation({});
        const singleLoc = locationsFromApi[0] ?? 'Default';
        const invLocSingle =
          inv?.locations?.find((x) => x.locationName === singleLoc) ?? inv?.locations?.[0];

        const fromSubmissionMap = new Map<string, { display: string; canonical: string }>();
        for (const line of inv?.lines ?? []) {
          const canonical = line.photoUrl?.trim() ?? '';
          if (canonical === '') continue;
          if (fromSubmissionMap.has(canonical)) continue;
          const display = resolvePairingScopedImageDisplayPath(canonical);
          fromSubmissionMap.set(canonical, { display, canonical });
        }
        const fromLinePhotos = Array.from(fromSubmissionMap.values()).map((v, photoIndex) => ({
          id: `loaded-${v.canonical.slice(0, 40)}`,
          displayUrl: v.display,
          canonicalUrl: v.canonical,
          aiAnalysisCompleted: true,
          persistedPhotoRef: {
            variant: 'stocktake' as const,
            locationIndex: 0,
            photoIndex,
          },
        }));
        const fromV2Only =
          fromLinePhotos.length === 0
            ? mapSubmissionLocationPhotoUrlsToStocktakeItems({
                locationKeyPrefix: 'loc0',
                locationIndex: Math.max(0, locationsFromApi.indexOf(singleLoc)),
                photoUrls: invLocSingle?.photoUrls ?? [],
                photoUrlsForDisplay: invLocSingle?.photoUrlsForDisplay,
                locationPhotos: invLocSingle?.locationPhotos,
              })
            : [];
        const mergedUploads = fromLinePhotos.length > 0 ? fromLinePhotos : fromV2Only;
        const firstPhotoUrl = mergedUploads[0]?.canonicalUrl ?? '';

        const next: Record<string, LineState> = {};
        for (const row of manualRows) {
          next[row.itemId] = {
            itemName: row.itemName,
            sortOrder: row.sortOrder,
            status: null,
            existingPhotoUrl: '',
            everMatchedByAi: false,
            aiRaw: {},
            ...defaultLineExpiry(),
          };
        }
        if (invLocSingle) {
          for (const item of invLocSingle.items) {
            const nk = `${item.itemName.trim().toLowerCase()}|||${item.categoryName.trim().toLowerCase()}`;
            const lk = `${singleLoc}|||${nk}`;
            const idKey = pairLocKeyToId.get(lk);
            if (!idKey) {
              continue;
            }
            const ar =
              item.aiRaw && typeof item.aiRaw === 'object'
                ? (item.aiRaw as Record<string, unknown>)
                : {};
            const fromAi = ar.stocktake === true;
            const itemPhoto = (item.photoUrl ?? '').trim();
            next[idKey] = {
              itemName: item.itemName.trim(),
              sortOrder: item.sortOrder,
              status: item.stockLevel,
              existingPhotoUrl: itemPhoto !== '' ? itemPhoto : firstPhotoUrl,
              everMatchedByAi: fromAi,
              aiRaw: { ...ar },
              ...hydrateExpiryFromSubmissionItem(item.expiries),
            };
          }
        }
        setLineMap(applyFirstPhotoFallbackToLines(next, firstPhotoUrl));
        setUploadedStocktakeItems(mergedUploads);
        setHelperScreen(
          data.status === APPROVAL_STATUS.NEEDS_UPDATE ? 'photos' : mergedUploads.length > 0 ? 'itemEdit' : 'photos',
        );
        setNoItemsByLocation({ [singleLoc]: [...(invLocSingle?.noItemsCategories ?? [])] });
      } else {
        const v2ItemsSingle = inv?.locations?.[0]?.items;
        const next: Record<string, LineState> = {};
        for (const row of effectiveTemplateRows) {
          const prev = inv?.lines.find((l) => l.itemId === row.itemId);
          const ar = prev?.aiRaw && typeof prev.aiRaw === 'object' ? (prev.aiRaw as Record<string, unknown>) : {};
          const fromAi = ar.stocktake === true;
          const v2Item =
            v2ItemsSingle?.find(
              (i) => i.itemName === row.itemName && i.categoryName === catLabelForRow(row.itemId),
            ) ?? v2ItemsSingle?.find((i) => i.itemName === row.itemName);
          next[row.itemId] = {
            itemName: row.itemName,
            sortOrder: row.sortOrder,
            status: prev ? prev.status : null,
            existingPhotoUrl: prev?.photoUrl ?? '',
            everMatchedByAi: fromAi,
            aiRaw: { ...ar },
            ...hydrateExpiryFromSubmissionItem(v2Item?.expiries),
          };
        }
        setLineMap(next);

        const fromSubmissionMap = new Map<string, { display: string; canonical: string }>();
        for (const line of inv?.lines ?? []) {
          const canonical = line.photoUrl?.trim() ?? '';
          if (canonical === '') continue;
          if (fromSubmissionMap.has(canonical)) continue;
          const display = resolvePairingScopedImageDisplayPath(canonical);
          fromSubmissionMap.set(canonical, { display, canonical });
        }
        const fromLinePhotos = Array.from(fromSubmissionMap.values()).map((v, photoIndex) => ({
          id: `loaded-${v.canonical.slice(0, 40)}`,
          displayUrl: v.display,
          canonicalUrl: v.canonical,
          aiAnalysisCompleted: true,
          persistedPhotoRef: {
            variant: 'stocktake' as const,
            locationIndex: 0,
            photoIndex,
          },
        }));
        const fromV2Only =
          fromLinePhotos.length === 0
            ? mapSubmissionLocationPhotoUrlsToStocktakeItems({
                locationKeyPrefix: 'loc0',
                locationIndex: 0,
                photoUrls: inv?.locations?.[0]?.photoUrls ?? [],
                photoUrlsForDisplay: inv?.locations?.[0]?.photoUrlsForDisplay,
                locationPhotos: inv?.locations?.[0]?.locationPhotos,
              })
            : [];
        const mergedUploads = fromLinePhotos.length > 0 ? fromLinePhotos : fromV2Only;
        const firstPhotoUrlTpl = mergedUploads[0]?.canonicalUrl ?? '';
        setLineMap(applyFirstPhotoFallbackToLines(next, firstPhotoUrlTpl));
        setUploadedStocktakeItems(mergedUploads);
        setHelperScreen(
          data.status === APPROVAL_STATUS.NEEDS_UPDATE ? 'photos' : mergedUploads.length > 0 ? 'itemEdit' : 'photos',
        );
      }

      const ca = inv?.completedAt ?? data.submission?.completedAt ?? '';
      if (ca && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(ca)) {
        const sp = splitDatetime(ca);
        setCompletedDate(sp.date);
        setCompletedTime(sp.time);
      } else {
        const tz = resolveDisplayTimeZone(user?.settings?.timezone);
        const n = splitDatetime(getNowDatetimeInTimezone(tz));
        setCompletedDate(n.date);
        setCompletedTime(n.time);
      }
    } catch {
      setError(t('task.review.loadError'));
    } finally {
      setLoading(false);
    }
  }, [instanceId, language, navigate, t, user?.role, user?.settings?.timezone]);

  useEffect(() => {
    setShowTaskContentOriginal(false);
  }, [language]);

  useEffect(() => {
    void fetchInstance();
  }, [fetchInstance]);

  useEffect(() => {
    if (!renameModalItemId) {
      return undefined;
    }
    const handleKey = (ev: Event): void => {
      if (!(ev instanceof KeyboardEvent)) return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setRenameModalItemId(null);
        setRenameDraft('');
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [renameModalItemId]);

  useEffect(() => {
    if (!instanceId || !taskInstance || !user) return;
    if (user.role !== USER_ROLE.HELPER) return;
    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    if (
      taskInstance.dueDate > todayYmd &&
      !isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
    ) {
      navigate(`/task-instances/${instanceId}`, { replace: true });
    }
  }, [instanceId, taskInstance, user, navigate]);

  useEffect(() => {
    if (!taskInstance || !user) return;
    if (user.role !== USER_ROLE.HELPER) return;
    if (taskInstance.status === APPROVAL_STATUS.PENDING && !taskInstance.submission?.submittedAt) {
      return;
    }
    if (taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE) {
      return;
    }
    navigate(`/task-instances/${instanceId}`, { replace: true });
  }, [taskInstance, user, navigate, instanceId]);

  useEffect(() => {
    if (helperScreen !== 'final') {
      setFinalReviewStatModalKind(null);
    }
  }, [helperScreen]);

  useEffect(() => {
    if (helperScreen !== 'final') {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
      const mainAppScroll = document.querySelector<HTMLElement>('[data-main-app-scroll]');
      mainAppScroll?.scrollTo({ top: 0, behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [helperScreen]);

  const resolveNewManualItemId = useCallback(async (name: string, categoryEffective: string): Promise<string> => {
    if (helperCanSaveItemsToMaster) {
      try {
        const res = await inventoryApi.createItem({ name, category: categoryEffective });
        if (res.success && res.data?.id) {
          return res.data.id;
        }
      } catch {
        // Use a local UUID for this submission if master create is unavailable.
      }
    }
    return typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `man-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }, [helperCanSaveItemsToMaster]);

  const handleRequestRemoveManualRow = useCallback((itemId: string) => {
    setRemoveManualItemId(itemId);
  }, []);

  const handleCancelRemoveManualRow = useCallback(() => {
    setRemoveManualItemId(null);
  }, []);

  const handleConfirmRemoveManualRow = useCallback(() => {
    if (!removeManualItemId) {
      return;
    }
    const id = removeManualItemId;
    setRemoveManualItemId(null);
    setManualTemplateRows((prev) => prev.filter((row) => row.itemId !== id));
    setLineMap((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    setLineMapsByLocation((prev) => {
      const next: Record<string, Record<string, LineState>> = { ...prev };
      for (const loc of Object.keys(next)) {
        const cur = next[loc];
        if (!cur || !(id in cur)) {
          continue;
        }
        const { [id]: _line, ...restLines } = cur;
        next[loc] = restLines;
      }
      return next;
    });
    setCategoryEditingItemId((cur) => (cur === id ? null : cur));
  }, [removeManualItemId]);

  const handleToggleManualAddSectionExpand = useCallback(() => {
    setManualAddSectionExpanded((prev) => !prev);
  }, []);

  const handleAddManualInventoryRow = (): void => {
    if (manualAddBusy) {
      return;
    }
    const nameTrim = manualAddItemName.trim();
    const catFromWizardPick = manualAddCategorySelect.trim();
    const catOther = manualAddCategoryOther.trim();
    const categoryEffective = wizardCategories.length > 0 ? catFromWizardPick : catOther;
    if (nameTrim === '' || categoryEffective === '') {
      setError(t('inventory.manualAdd.validationBoth'));
      return;
    }
    if (wizardCategories.length > 0 && !wizardCategories.includes(categoryEffective)) {
      setError(t('inventory.manualAdd.validationCategory'));
      return;
    }
    if (manualTemplateRows.length >= maxManualInventoryTemplateRows) {
      setError(t('inventory.manualAdd.limitReached', { max: maxManualInventoryTemplateRows }));
      return;
    }
    if (manualAddStatus === null) {
      setError(t('inventory.manualAdd.stockRequired'));
      return;
    }
    const activePhotosForManual = isMultiLocationWizard
      ? photosByLocation[wizardLocations[locationStepIndex] ?? ''] ?? []
      : uploadedStocktakeItems;
    const autoPhoto =
      activePhotosForManual.length === 1 ? (activePhotosForManual[0]?.canonicalUrl.trim() ?? '') : '';
    const pickedPhoto =
      autoPhoto !== ''
        ? autoPhoto
        : activePhotosForManual.find((p) => p.id === manualAddPhotoId)?.canonicalUrl.trim() ?? '';
    if (pickedPhoto === '') {
      setError(t('inventory.manualAdd.photoRequired'));
      return;
    }
    if (manualAddUseNoExpiry === null) {
      setError(t('inventory.manualAdd.expiryChoiceRequired'));
      return;
    }

    const expiryList: string[] = [];
    if (manualAddUseNoExpiry === false) {
      const sortedUnique = [
        ...new Set(manualAddExpiryYmdList.map((d) => d.trim()).filter((d) => EXPIRY_YMD_REGEX.test(d))),
      ].sort();
      if (sortedUnique.length === 0) {
        setError(t('inventory.expiry.required', { item: nameTrim }));
        return;
      }
      expiryList.push(...sortedUnique);
    }

    const sortOrderSeed =
      stocktakeRowsOrdered.reduce((acc, row) => Math.max(acc, row.sortOrder), -1) + 1;
    const manualAddTargetLoc = wizardLocations[locationStepIndex] ?? wizardLocations[0] ?? '';

    setManualAddBusy(true);
    void (async () => {
      try {
        const newId = await resolveNewManualItemId(nameTrim, categoryEffective);
        const newRow: ManualInventoryTemplateRowMeta = {
          itemId: newId,
          itemName: nameTrim,
          categoryName: categoryEffective,
          sortOrder: sortOrderSeed,
          ...(isMultiLocationWizard && manualAddTargetLoc !== ''
            ? { introducedLocation: manualAddTargetLoc }
            : {}),
        };
        const lineSeed: LineState = {
          itemName: nameTrim,
          sortOrder: sortOrderSeed,
          status: manualAddStatus,
          existingPhotoUrl: pickedPhoto,
          everMatchedByAi: false,
          aiRaw: {},
          useNoExpiry: manualAddUseNoExpiry,
          expiryYmdList: expiryList,
        };

        setManualTemplateRows((prev) => [...prev, newRow]);

        if (isMultiLocationWizard) {
          setLineMapsByLocation((prev) => {
            const tgt = manualAddTargetLoc;
            const cur = { ...(prev[tgt] ?? {}) };
            cur[newId] = { ...lineSeed };
            return { ...prev, [tgt]: cur };
          });
        } else {
          setLineMap((prev) => ({
            ...prev,
            [newId]: { ...lineSeed },
          }));
        }

        setManualAddItemName('');
        setManualAddCategorySelect('');
        setManualAddCategoryOther('');
        setManualAddStatus(null);
        setManualAddUseNoExpiry(null);
        setManualAddExpiryYmdList([]);
        setManualAddPhotoId('');
        setError(null);
        clearNoItemsMarkForCategoryAtActiveLocation(categoryEffective);
      } catch {
        setError(t('inventory.manualAdd.addFailed'));
      } finally {
        setManualAddBusy(false);
      }
    })();
  };

  const handleSetStatus = (itemId: string, status: InventoryLevel) => {
    clearNoItemsMarkForItemCategory(itemId);
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row) {
          return prev;
        }
        cur[itemId] = { ...row, status };
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], status },
    }));
  };

  const handleSetUseNoExpiry = (itemId: string, nextUseNoExpiry: boolean) => {
    clearNoItemsMarkForItemCategory(itemId);
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row) {
          return prev;
        }
        if (nextUseNoExpiry) {
          cur[itemId] = { ...row, useNoExpiry: true, expiryYmdList: [] };
        } else {
          const list = row.expiryYmdList.length > 0 ? [...row.expiryYmdList] : [''];
          cur[itemId] = { ...row, useNoExpiry: false, expiryYmdList: list };
        }
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => {
      const row = prev[itemId];
      if (!row) {
        return prev;
      }
      if (nextUseNoExpiry) {
        return { ...prev, [itemId]: { ...row, useNoExpiry: true, expiryYmdList: [] } };
      }
      const list = row.expiryYmdList.length > 0 ? [...row.expiryYmdList] : [''];
      return { ...prev, [itemId]: { ...row, useNoExpiry: false, expiryYmdList: list } };
    });
  };

  const handleSetLineItemPhoto = (itemId: string, photoId: string): void => {
    const photos = getActiveLocationPhotos();
    const photo = photos.find((p) => p.id === photoId);
    if (!photo) {
      return;
    }
    const canonical = photo.canonicalUrl.trim();
    if (canonical === '') {
      return;
    }
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row) {
          return prev;
        }
        cur[itemId] = { ...row, existingPhotoUrl: canonical };
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => {
      const row = prev[itemId];
      if (!row) {
        return prev;
      }
      return { ...prev, [itemId]: { ...row, existingPhotoUrl: canonical } };
    });
  };

  const handleAddExpiryRow = (itemId: string) => {
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row || row.useNoExpiry !== false || row.expiryYmdList.length >= maxExpiryDatesPerItem) {
          return prev;
        }
        cur[itemId] = { ...row, expiryYmdList: [...row.expiryYmdList, ''] };
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => {
      const row = prev[itemId];
      if (!row || row.useNoExpiry !== false || row.expiryYmdList.length >= maxExpiryDatesPerItem) {
        return prev;
      }
      return { ...prev, [itemId]: { ...row, expiryYmdList: [...row.expiryYmdList, ''] } };
    });
  };

  const handleRemoveExpiryRow = (itemId: string, index: number) => {
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row || row.useNoExpiry !== false) {
          return prev;
        }
        const nextList = row.expiryYmdList.filter((_, i) => i !== index);
        cur[itemId] = {
          ...row,
          expiryYmdList: nextList.length > 0 ? nextList : [''],
        };
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => {
      const row = prev[itemId];
      if (!row || row.useNoExpiry !== false) {
        return prev;
      }
      const nextList = row.expiryYmdList.filter((_, i) => i !== index);
      return {
        ...prev,
        [itemId]: { ...row, expiryYmdList: nextList.length > 0 ? nextList : [''] },
      };
    });
  };

  const handleExpiryYmdChange = (itemId: string, index: number, nextYmd: string) => {
    clearNoItemsMarkForItemCategory(itemId);
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row || row.useNoExpiry !== false) {
          return prev;
        }
        const nextList = row.expiryYmdList.map((d, i) => (i === index ? nextYmd : d));
        cur[itemId] = { ...row, expiryYmdList: nextList };
        return { ...prev, [loc]: cur };
      });
      return;
    }
    setLineMap((prev) => {
      const row = prev[itemId];
      if (!row || row.useNoExpiry !== false) {
        return prev;
      }
      const nextList = row.expiryYmdList.map((d, i) => (i === index ? nextYmd : d));
      return { ...prev, [itemId]: { ...row, expiryYmdList: nextList } };
    });
  };

  const pruneOrphanManualRows = useCallback((): void => {
    setManualTemplateRows((prev) => {
      const orphanIds = new Set<string>();
      if (isMultiLocationWizard) {
        for (const loc of wizardLocations) {
          const lm = lineMapsByLocation[loc] ?? {};
          for (const id of collectOrphanManualRowIdsForLocation(
            prev,
            manualItemIdSet,
            wizardLocations,
            loc,
            lm,
          )) {
            orphanIds.add(id);
          }
        }
      } else {
        const loc = wizardLocations[0] ?? 'Default';
        for (const id of collectOrphanManualRowIdsForLocation(
          prev,
          manualItemIdSet,
          wizardLocations,
          loc,
          lineMap,
        )) {
          orphanIds.add(id);
        }
      }
      if (orphanIds.size === 0) {
        return prev;
      }
      return prev.filter((row) => !orphanIds.has(row.itemId));
    });
  }, [isMultiLocationWizard, lineMap, lineMapsByLocation, manualItemIdSet, wizardLocations]);

  const runStocktakeAnalysisForPhotoIds = async (photoIds: string[]): Promise<boolean> => {
    if (!instanceId || photoIds.length === 0) {
      return true;
    }
    setBulkAnalyzing(true);
    setBulkBusyMode('analyze');
    setBulkError(null);
    setError(null);
    setPageRateInfo(null);
    let analysisSucceeded = false;
    try {
      const usageRes = await settingsApi.getAiUsage();
      const usage = usageRes.data?.ai;
      if (
        emitDailyQuotaLimitIfInsufficient(
          usage,
          photoIds.length,
          LIMIT_ERROR_CODE.DAILY_AI_ANALYZE_LIMIT_REACHED,
          'photo',
        )
      ) {
        return false;
      }

      const analyzeLocationName =
        wizardLocationsRef.current[locationStepIndexRef.current] ?? wizardLocationsRef.current[0] ?? '';
      const activePhotos = getActiveLocationPhotos();
      const targets: StocktakeAnalyzeTarget[] = [];
      const analyzedPhotoIds: string[] = [];
      for (const photoId of photoIds) {
        const photo = activePhotos.find((p) => p.id === photoId);
        if (!photo) {
          continue;
        }
        const canonical = photo.canonicalUrl.trim();
        if (canonical !== '') {
          targets.push({ kind: 'stored', canonicalUrl: canonical });
          analyzedPhotoIds.push(photoId);
          continue;
        }
        const pending = pendingUploadBlobsRef.current.get(photoId);
        if (pending) {
          targets.push({ kind: 'pending', blob: pending.blob, fileName: pending.fileName });
          analyzedPhotoIds.push(photoId);
        }
      }
      if (targets.length === 0) {
        return true;
      }

      const results = await analyzeSelectedStocktakePhotos(
        targets,
        instanceId,
        (current, total) => {
          setBulkProgress({ current, total });
        },
        { locationName: analyzeLocationName },
      );

      const quotaHit = results.some((d) => d.analysisSkippedReason === 'daily_ai_limit');
      if (quotaHit) {
        setBulkError(t('inventory.stocktakeDailyAiLimitBatchCancelled'));
        return false;
      }
      const lastError = results.find((d) => !d.analysisOk);
      if (lastError !== undefined) {
        if (lastError.analysisSkippedReason === 'daily_ai_limit') {
          setBulkError(t('inventory.stocktakeDailyAiLimitPhotoSaved'));
        } else {
          setBulkError(lastError.analysisError ?? t('inventory.analyzeError'));
        }
      }

      const isDiscoveryResponse = (data: (typeof results)[number]): boolean => {
        if (data.mode === 'discovery') return true;
        if (data.mode === 'catalog') return false;
        return (data.discoveryAssignments?.length ?? 0) > 0;
      };

      const pairedScopeRows =
        wizardLocationsRef.current.length > 1
          ? filterStocktakeRowsForWizardLocation(
              stocktakeRowsOrdered,
              manualTemplateRows,
              manualItemIdSet,
              wizardLocationsRef.current,
              analyzeLocationName,
            )
          : stocktakeRowsOrdered;

      let sortSeed =
        stocktakeRowsOrdered.reduce((acc, row) => Math.max(acc, row.sortOrder), -1) + 1;
      const lineMapForPairing =
        wizardLocationsRef.current.length > 1
          ? (lineMapsByLocationRef.current[analyzeLocationName] ?? {})
          : lineMapRef.current;
      const pairedKeys = new Set(
        pairedScopeRows
          .filter((row) => Boolean(lineMapForPairing[row.itemId]))
          .map(
            (r) =>
              `${getRowCategory(r.itemId).trim().toLowerCase()}|||${r.itemName.trim().toLowerCase()}`,
          ),
      );
      const discoveryRows: ManualInventoryTemplateRowMeta[] = [];
      const discoveryLineById: Record<string, LineState> = {};
      const categoriesWithStockResult = new Set<string>();

      for (const data of results) {
        if (!isDiscoveryResponse(data)) {
          continue;
        }
        const url = data.storedImageUrl?.trim() ?? '';
        for (const assign of data.discoveryAssignments ?? []) {
          for (const it of assign.items) {
            const pairKey = `${assign.categoryName.trim().toLowerCase()}|||${it.itemName.trim().toLowerCase()}`;
            if (pairedKeys.has(pairKey)) {
              continue;
            }
            pairedKeys.add(pairKey);
            const newId = await resolveNewManualItemId(it.itemName, assign.categoryName);
            const so = sortSeed;
            sortSeed += 1;
            discoveryRows.push({
              itemId: newId,
              itemName: it.itemName,
              categoryName: assign.categoryName,
              sortOrder: so,
              ...(wizardLocationsRef.current.length > 1 ? { introducedLocation: analyzeLocationName } : {}),
            });
            const statusFromAi =
              it.status === null || it.status === undefined
                ? null
                : parseInventoryStatus(it.status);
            if (statusFromAi !== null) {
              categoriesWithStockResult.add(assign.categoryName.trim());
            }
            discoveryLineById[newId] = {
              itemName: it.itemName,
              sortOrder: so,
              status: statusFromAi,
              existingPhotoUrl: url,
              everMatchedByAi: true,
              aiRaw: {
                stocktake: true,
                discovery: true,
                matchConfidence: it.confidence,
                analysisOk: data.analysisOk,
                analysisError: data.analysisError,
              },
              itemNameEmployerDisplay: it.itemNameEmployer?.trim() || it.itemName,
              itemNameHelperSnapshot: it.itemName,
              ...defaultLineExpiry(),
            };
          }
        }
      }

      if (discoveryRows.length > 0) {
        setManualTemplateRows((prev) => [...prev, ...discoveryRows]);
        if (wizardLocationsRef.current.length > 1) {
          const activeLoc =
            wizardLocationsRef.current[locationStepIndexRef.current] ??
            wizardLocationsRef.current[0] ??
            '';
          setLineMapsByLocation((prev) => {
            const cur = { ...(prev[activeLoc] ?? {}) };
            for (const row of discoveryRows) {
              const picked = discoveryLineById[row.itemId];
              if (picked) {
                cur[row.itemId] = picked;
              }
            }
            return { ...prev, [activeLoc]: cur };
          });
        } else {
          setLineMap((prev) => {
            const next = { ...prev };
            for (const row of discoveryRows) {
              const picked = discoveryLineById[row.itemId];
              if (picked) {
                next[row.itemId] = picked;
              }
            }
            return next;
          });
        }
      }

      if (wizardLocationsRef.current.length > 1) {
        const loc =
          wizardLocationsRef.current[locationStepIndexRef.current] ?? wizardLocationsRef.current[0] ?? '';
        setLineMapsByLocation((prev) => {
          const nextMaps = { ...prev };
          const next = { ...(nextMaps[loc] ?? {}) };
          for (const data of results) {
            if (isDiscoveryResponse(data)) {
              continue;
            }
            const url = data.storedImageUrl?.trim() ?? '';
            for (const m of data.matches) {
              const cur = next[m.itemId];
              if (!cur) continue;
              const st = parseInventoryStatus(m.status) ?? cur.status;
              if (st !== null) {
                categoriesWithStockResult.add(getRowCategory(m.itemId).trim());
              }
              next[m.itemId] = {
                ...cur,
                status: st,
                existingPhotoUrl: url !== '' ? url : cur.existingPhotoUrl,
                everMatchedByAi: true,
                aiRaw: {
                  ...cur.aiRaw,
                  stocktake: true,
                  matchConfidence: m.confidence,
                  analysisOk: data.analysisOk,
                  analysisError: data.analysisError,
                  lastNotFoundItemIds: data.notFoundItemIds,
                },
              };
            }
          }
          nextMaps[loc] = next;
          return nextMaps;
        });
        setPhotosByLocation((prev) => {
          const analyzedIdSet = new Set(analyzedPhotoIds);
          return {
            ...prev,
            [loc]: (prev[loc] ?? []).map((photo) =>
              analyzedIdSet.has(photo.id) ? { ...photo, aiAnalysisCompleted: true } : photo,
            ),
          };
        });
      } else {
        setLineMap((prev) => {
          const next = { ...prev };
          for (const data of results) {
            if (isDiscoveryResponse(data)) {
              continue;
            }
            const url = data.storedImageUrl?.trim() ?? '';
            for (const m of data.matches) {
              const cur = next[m.itemId];
              if (!cur) continue;
              const st = parseInventoryStatus(m.status) ?? cur.status;
              if (st !== null) {
                categoriesWithStockResult.add(getRowCategory(m.itemId).trim());
              }
              next[m.itemId] = {
                ...cur,
                status: st,
                existingPhotoUrl: url !== '' ? url : cur.existingPhotoUrl,
                everMatchedByAi: true,
                aiRaw: {
                  ...cur.aiRaw,
                  stocktake: true,
                  matchConfidence: m.confidence,
                  analysisOk: data.analysisOk,
                  analysisError: data.analysisError,
                  lastNotFoundItemIds: data.notFoundItemIds,
                },
              };
            }
          }
          return next;
        });
        setUploadedStocktakeItems((prev) => {
          const analyzedIdSet = new Set(analyzedPhotoIds);
          return prev.map((photo) =>
            analyzedIdSet.has(photo.id) ? { ...photo, aiAnalysisCompleted: true } : photo,
          );
        });
      }
      if (categoriesWithStockResult.size > 0 && analyzeLocationName !== '') {
        setNoItemsByLocation((prev) => {
          const cur = prev[analyzeLocationName] ?? [];
          const next = removeCategoriesFromNoItemsList(cur, [...categoriesWithStockResult]);
          if (next.length === cur.length) {
            return prev;
          }
          return { ...prev, [analyzeLocationName]: next };
        });
      }
      analysisSucceeded = true;
      pruneOrphanManualRows();
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return false;
      }
      if (caught instanceof InventoryStocktakeHttpError) {
        const b = caught.body;
        if (caught.status === 429) {
          return false;
        }
        setBulkError(typeof b.error === 'string' ? b.error : t('inventory.analyzeError'));
      } else {
        setBulkError(t('inventory.analyzeError'));
      }
    } finally {
      setBulkAnalyzing(false);
      setBulkBusyMode(null);
      setBulkProgress({ current: 0, total: 0 });
    }
    return analysisSucceeded;
  };

  const purgeItemsForCanonicalPhotos = (canonicalUrls: string[]): void => {
    const canonSet = new Set(canonicalUrls.map((u) => u.trim()).filter((u) => u !== ''));
    if (canonSet.size === 0) {
      return;
    }
    const manualIdsToRemoveAll: string[] = [];
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      const cur = { ...(lineMapsByLocation[loc] ?? {}) };
      let nextMap = cur;
      for (const canonical of canonSet) {
        const purged = purgeLineDataForCanonicalPhoto(nextMap, manualItemIdSet, canonical);
        nextMap = purged.lineMap;
        manualIdsToRemoveAll.push(...purged.manualIdsToRemove);
      }
      setLineMapsByLocation((prev) => ({ ...prev, [loc]: nextMap }));
    } else {
      let nextMap = lineMap;
      for (const canonical of canonSet) {
        const purged = purgeLineDataForCanonicalPhoto(nextMap, manualItemIdSet, canonical);
        nextMap = purged.lineMap;
        manualIdsToRemoveAll.push(...purged.manualIdsToRemove);
      }
      setLineMap(nextMap);
    }
    if (manualIdsToRemoveAll.length > 0) {
      const removeSet = new Set(manualIdsToRemoveAll);
      setManualTemplateRows((prev) => prev.filter((r) => !removeSet.has(r.itemId)));
    }
    pruneOrphanManualRows();
  };

  useEffect(() => {
    pruneOrphanManualRows();
  }, [lineMap, lineMapsByLocation, manualTemplateRows, pruneOrphanManualRows]);

  const photosNeedingReanalyzeConfirm = (photoIds: string[]): { photoCount: number; itemCount: number } => {
    const photos = getActiveLocationPhotos().filter(
      (p) => photoIds.includes(p.id) && p.aiAnalysisCompleted,
    );
    const canonUrls = photos.map((p) => p.canonicalUrl);
    const loc = isMultiLocationWizard ? lineMapsByLocation[wizardLocations[locationStepIndex] ?? ''] ?? {} : lineMap;
    const itemCount = canonUrls.reduce((acc, url) => acc + countLinesLinkedToCanonical(loc, url), 0);
    return { photoCount: photos.length, itemCount };
  };

  const handleContinueWithoutAi = (): void => {
    handleContinueFromPhotosStep();
  };

  const handleOpenAiAnalyzeFlow = (): void => {
    const photos = getActiveLocationPhotos();
    const hasPhoto = photos.some((p) => p.canonicalUrl.trim() !== '');
    if (!hasPhoto) {
      setError(t('inventory.needAtLeastOneBatchPhoto'));
      return;
    }
    setError(null);
    const hasAlreadyAnalyzed = photos.some((p) => p.aiAnalysisCompleted);
    if (!hasAlreadyAnalyzed) {
      const defaultSelectedIds = photos
        .filter((p) => !p.aiAnalysisCompleted && p.canonicalUrl.trim() !== '')
        .map((p) => p.id);
      handleAiPhotoSelectConfirm(defaultSelectedIds);
      return;
    }
    setAiPhotoSelectModalOpen(true);
  };

  const handleAiPhotoSelectConfirm = (selectedPhotoIds: string[]): void => {
    setAiPhotoSelectModalOpen(false);
    if (selectedPhotoIds.length === 0) {
      handleContinueFromPhotosStep();
      return;
    }
    const { itemCount } = photosNeedingReanalyzeConfirm(selectedPhotoIds);
    if (itemCount > 0) {
      setPendingAiPhotoIds(selectedPhotoIds);
      setReanalyzeConfirmOpen(true);
      return;
    }
    void (async () => {
      const ok = await runStocktakeAnalysisForPhotoIds(selectedPhotoIds);
      if (ok) {
        setHelperScreen('itemEdit');
      }
    })();
  };

  const handleReanalyzeConfirmProceed = (): void => {
    const photoIds = pendingAiPhotoIds;
    setReanalyzeConfirmOpen(false);
    setPendingAiPhotoIds([]);
    const photosToPurge = getActiveLocationPhotos().filter(
      (p) => photoIds.includes(p.id) && p.aiAnalysisCompleted,
    );
    purgeItemsForCanonicalPhotos(photosToPurge.map((p) => p.canonicalUrl));
    void (async () => {
      const ok = await runStocktakeAnalysisForPhotoIds(photoIds);
      if (ok) {
        setHelperScreen('itemEdit');
      }
    })();
  };

  const handlePickStocktakeFiles = async (fileList: File[]) => {
    if (taskInstance?.inventoryChainBlockedReason) {
      setBulkError(t('inventory.chainBlockedBanner'));
      return;
    }
    if (fileList.length === 0) return;
    if (fileList.length > maxStocktakeFiles) {
      setBulkError(t('receipt.analyze.tooManyFiles'));
      return;
    }
    const compressed: CompressedReceiptBlob[] = [];
    for (const f of fileList) {
      const v = validateReceiptFile(f);
      if (v === 'tooLarge') {
        setBulkError(t('receipt.analyze.fileTooLarge'));
        return;
      }
      if (v === 'badType') {
        setBulkError(t('receipt.analyze.badType'));
        return;
      }
      try {
        compressed.push(await compressImageFile(f));
      } catch {
        const mime = resolveClientImageMime(f);
        if (!mime) {
          setBulkError(t('receipt.analyze.badType'));
          return;
        }
        compressed.push({ blob: f, fileName: f.name, mime });
      }
    }
    await uploadCompressedToActiveLocation(compressed);
  };

  const getActiveLocationPhotos = (): StocktakeUploadedItem[] =>
    isMultiLocationWizard
      ? photosByLocation[wizardLocations[locationStepIndex] ?? ''] ?? []
      : uploadedStocktakeItems;

  const setActiveLocationPhotos = (photos: StocktakeUploadedItem[]): void => {
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setPhotosByLocation((prev) => ({ ...prev, [loc]: photos }));
      return;
    }
    setUploadedStocktakeItems(photos);
  };

  const uploadCompressedToActiveLocation = async (compressed: CompressedReceiptBlob[]): Promise<void> => {
    if (!instanceId) {
      return;
    }
    setBulkAnalyzing(true);
    setBulkBusyMode('upload');
    setBulkProgress({ current: 0, total: compressed.length });
    setBulkError(null);
    setError(null);
    try {
      const analyzeLocationName =
        wizardLocationsRef.current[locationStepIndexRef.current] ?? wizardLocationsRef.current[0] ?? '';
      const curPhotos = [...getActiveLocationPhotos()];
      const seenCanon = new Set(curPhotos.map((p) => p.canonicalUrl));
      for (let uploadIndex = 0; uploadIndex < compressed.length; uploadIndex += 1) {
        const c = compressed[uploadIndex];
        setBulkProgress({ current: uploadIndex + 1, total: compressed.length });
        const uploaded = await uploadInventoryStocktake(c.blob, c.fileName, instanceId, {
          locationName: analyzeLocationName,
        });
        const canonical = uploaded.storedImageUrl?.trim() ?? '';
        const display = resolvePairingScopedImageDisplayPath(canonical);
        if (canonical === '' || seenCanon.has(canonical)) {
          continue;
        }
        seenCanon.add(canonical);
        const photoId = newStocktakeUploadId();
        const localPreviewUrl = URL.createObjectURL(c.blob);
        pendingUploadBlobsRef.current.set(photoId, c);
        curPhotos.push({
          id: photoId,
          displayUrl: display || canonical,
          canonicalUrl: canonical,
          localPreviewUrl,
          aiAnalysisCompleted: false,
        });
      }
      setActiveLocationPhotos(curPhotos);
    } catch (caught) {
      setBulkError(caught instanceof Error ? caught.message : t('inventory.analyzeError'));
    } finally {
      setBulkAnalyzing(false);
      setBulkBusyMode(null);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  const handleStocktakeFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(event.target.files ?? []);
    event.target.value = '';
    await handlePickStocktakeFiles(fileList);
  };

  const handleCameraPickClick = () => {
    if (bulkAnalyzing) return;
    cameraInputRef.current?.click();
  };

  const handleGalleryPickClick = () => {
    if (bulkAnalyzing) return;
    galleryInputRef.current?.click();
  };

  const handleRequestRemoveStocktakeImage = (item: StocktakeUploadedItem) => {
    setRemoveConfirmItem(item);
  };

  const removePhotoLinkedItemCount = useMemo(() => {
    if (!removeConfirmItem) {
      return 0;
    }
    const loc = isMultiLocationWizard ? lineMapsByLocation[wizardLocations[locationStepIndex] ?? ''] ?? {} : lineMap;
    return countLinesLinkedToCanonical(loc, removeConfirmItem.canonicalUrl);
  }, [isMultiLocationWizard, lineMap, lineMapsByLocation, locationStepIndex, removeConfirmItem, wizardLocations]);

  const handleCancelRemoveStocktakePhotoDialog = (): void => {
    setRemoveConfirmItem(null);
  };

  const handleKeepPhotoAndItems = (): void => {
    setRemoveConfirmItem(null);
  };

  const handleDeletePhotoAndLinkedItems = (): void => {
    const item = removeConfirmItem;
    if (!item) {
      return;
    }
    const canonical = item.canonicalUrl;
    if (isMultiLocationWizard) {
      const loc = wizardLocations[locationStepIndex] ?? '';
      setPhotosByLocation((prev) => {
        const remaining = (prev[loc] ?? []).filter((p) => p.id !== item.id);
        revokeStocktakeLocalPreviewIfUnused(remaining, item.localPreviewUrl);
        return { ...prev, [loc]: remaining };
      });
    } else {
      setUploadedStocktakeItems((prev) => {
        const remaining = prev.filter((p) => p.id !== item.id);
        revokeStocktakeLocalPreviewIfUnused(remaining, item.localPreviewUrl);
        return remaining;
      });
    }
    pendingUploadBlobsRef.current.delete(item.id);
    purgeItemsForCanonicalPhotos([canonical]);
    setRemoveConfirmItem(null);
  };

  const isRowSkippedForNoItems = (locationName: string, itemId: string): boolean => {
    const noSet = new Set(noItemsByLocation[locationName] ?? []);
    return noSet.has(getRowCategory(itemId));
  };

  const validateCategoriesForLocation = useCallback(
    (
      locationName: string,
      lm: Record<string, LineState>,
      options?: {
        deferEmptyWithoutNoItemsTick?: boolean;
      },
    ): string | null => {
      const rowsHere = filterStocktakeRowsForWizardLocation(
        stocktakeRowsOrdered,
        manualTemplateRows,
        manualItemIdSet,
        wizardLocations,
        locationName,
      );
      const noSet = new Set(noItemsByLocation[locationName] ?? []);
      const checkExpiries = (): string | null => {
        for (const row of rowsHere) {
          if (noSet.has(getRowCategory(row.itemId))) {
            continue;
          }
          const st = lm[row.itemId];
          if (!st || st.status === null) {
            continue;
          }
          if (st.useNoExpiry === null) {
            return t('inventory.expiry.choiceRequired', { item: st.itemName });
          }
          if (st.useNoExpiry === true) {
            continue;
          }
          const valid = (st.expiryYmdList ?? []).map((d) => d.trim()).filter((d) => EXPIRY_YMD_REGEX.test(d));
          if (valid.length === 0) {
            return t('inventory.expiry.required', { item: st.itemName });
          }
        }
        return null;
      };
      if (wizardCategories.length > 0) {
        for (const cat of wizardCategories) {
          const rowsInCat = rowsHere.filter((r) => getRowCategory(r.itemId) === cat);
          if (noSet.has(cat)) {
            const conflict = rowsInCat.some((r) => lm[r.itemId]?.status !== null);
            if (conflict) {
              return t('inventory.wizard.noItemsConflict');
            }
            continue;
          }
          if (rowsInCat.length === 0) {
            if (options?.deferEmptyWithoutNoItemsTick) {
              continue;
            }
            return t('inventory.wizard.categoryMarkNoItems', {
              category: resolveWizardCategoryLabel(cat),
            });
          }
          for (const r of rowsInCat) {
            if (lm[r.itemId]?.status === null) {
              return t('inventory.stockLevelRequired');
            }
          }
        }
        return checkExpiries();
      }
      for (const row of rowsHere) {
        if (lm[row.itemId]?.status === null) {
          return t('inventory.stockLevelRequired');
        }
      }
      return checkExpiries();
    },
    [
      getRowCategory,
      manualItemIdSet,
      manualTemplateRows,
      noItemsByLocation,
      resolveWizardCategoryLabel,
      t,
      stocktakeRowsOrdered,
      wizardCategories,
      wizardLocations,
    ],
  );

  const multiLocationHubEntries = useMemo(() => {
    if (!isMultiLocationWizard) {
      return [] as Array<{
        locationName: string;
        index: number;
        hasPhoto: boolean;
        allRowsPresent: boolean;
        readyForReview: boolean;
        statusLineKey: string;
      }>;
    }
    return wizardLocations.map((locationName, index) => {
      const lm = lineMapsByLocation[locationName] ?? {};
      const photos = photosByLocation[locationName] ?? [];
      const hasPhoto = photos.some((p) => p.canonicalUrl.trim() !== '');
      const rowsHere = filterStocktakeRowsForWizardLocation(
        stocktakeRowsOrdered,
        manualTemplateRows,
        manualItemIdSet,
        wizardLocations,
        locationName,
      );
      const allRowsPresent = rowsHere.every((r) => Boolean(lm[r.itemId]));
      const catOk = validateCategoriesForLocation(locationName, lm);
      const readyForReview = hasPhoto && allRowsPresent && catOk === null;
      const statusLineKey = resolveLocationHubStatusLineKey({
        hasPhoto,
        allRowsPresent,
        readyForReview,
        lineMap: lm,
        rowsHere,
        wizardCategories,
        noItemsCategories: noItemsByLocation[locationName] ?? [],
        getRowCategory,
      });
      return {
        locationName,
        index,
        hasPhoto,
        allRowsPresent,
        readyForReview,
        statusLineKey,
      };
    });
  }, [
    getRowCategory,
    isMultiLocationWizard,
    lineMapsByLocation,
    manualItemIdSet,
    manualTemplateRows,
    noItemsByLocation,
    photosByLocation,
    stocktakeRowsOrdered,
    validateCategoriesForLocation,
    wizardCategories,
    wizardLocations,
  ]);

  const multiLocationReadyCount = useMemo(
    () => multiLocationHubEntries.filter((e) => e.readyForReview).length,
    [multiLocationHubEntries],
  );

  const finalReviewStats = useMemo(() => {
    type LowStockSummaryRow = {
      itemName: string;
      categoryName: string;
      locationName: string;
      stockLevel: InventoryLevel;
    };
    type ExpiringSummaryRow = {
      itemName: string;
      categoryName: string;
      locationName: string;
      detailLine: string;
    };

    const empty = {
      lowStock: 0,
      expiringSoon: 0,
      expiringWindowDays: 7,
      lowStockRows: [] as LowStockSummaryRow[],
      expiringRows: [] as ExpiringSummaryRow[],
    };

    if (!taskInstance || !user) {
      return empty;
    }

    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    const notifyExpiryLeadDays = taskInstance.task.inventoryNotifyExpiryLeadDays;
    const expiringWindowDays = resolveInventoryExpiringSoonWindowDays(notifyExpiryLeadDays);
    const scanLocs = isMultiLocationWizard ? wizardLocations : [wizardLocations[0] ?? 'Default'];

    const expiryCaptionFor = (d: string): string => {
      const daysUntil = diffCalendarDaysYmd(todayYmd, d);
      if (daysUntil < 0) {
        return t('task.review.inventoryExpiryExpired');
      }
      if (daysUntil >= 30) {
        return t('task.review.inventoryExpiryAbsolute', { date: d });
      }
      if (daysUntil === 0) {
        return t('task.review.inventoryExpiryToday');
      }
      if (daysUntil === 1) {
        return t('task.review.inventoryExpiryTomorrow');
      }
      return t('task.review.inventoryExpiryInDays', { count: daysUntil });
    };

    const lowStockRows: LowStockSummaryRow[] = [];
    const expiringRows: ExpiringSummaryRow[] = [];

    for (const loc of scanLocs) {
      const lm = isMultiLocationWizard ? lineMapsByLocation[loc] ?? {} : lineMap;
      const rowsHere = filterStocktakeRowsForWizardLocation(
        stocktakeRowsOrdered,
        manualTemplateRows,
        manualItemIdSet,
        wizardLocations,
        loc,
      );
      for (const row of rowsHere) {
        if (isRowSkippedForNoItems(loc, row.itemId)) {
          continue;
        }
        const st = lm[row.itemId];
        if (!st || st.status === null) {
          continue;
        }

        const itemName = st.itemName.trim();
        const categoryName = getRowCategory(row.itemId).trim();
        const locationName = loc.trim();

        if (st.status === INVENTORY_LEVEL.LOW || st.status === INVENTORY_LEVEL.EMPTY) {
          lowStockRows.push({
            itemName,
            categoryName,
            locationName,
            stockLevel: st.status,
          });
        }

        if (st.useNoExpiry === false) {
          const qualifyingDates: string[] = [];
          for (const exp of st.expiryYmdList) {
            const d = exp.trim();
            if (!EXPIRY_YMD_REGEX.test(d)) {
              continue;
            }
            if (!shouldSurfaceInventoryExpiryForSummary(todayYmd, d, notifyExpiryLeadDays)) {
              continue;
            }
            qualifyingDates.push(d);
          }
          if (qualifyingDates.length > 0) {
            const sortedUnique = [...new Set(qualifyingDates)].sort((a, b) => a.localeCompare(b));
            const detailLine = sortedUnique.map((d) => `${d} (${expiryCaptionFor(d)})`).join(' � ');
            expiringRows.push({ itemName, categoryName, locationName, detailLine });
          }
        }
      }
    }

    const sortKey = (r: {
      locationName: string;
      categoryName: string;
      itemName: string;
    }): string => `${r.locationName}\u0000${r.categoryName}\u0000${r.itemName}`;
    lowStockRows.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    expiringRows.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

    return {
      lowStock: lowStockRows.length,
      expiringSoon: expiringRows.length,
      expiringWindowDays,
      lowStockRows,
      expiringRows,
    };
  }, [
    getRowCategory,
    isMultiLocationWizard,
    isRowSkippedForNoItems,
    lineMap,
    lineMapsByLocation,
    manualItemIdSet,
    manualTemplateRows,
    stocktakeRowsOrdered,
    t,
    taskInstance,
    user,
    wizardLocations,
  ]);

  const handleCloseFinalReviewStatModal = useCallback(() => {
    setFinalReviewStatModalKind(null);
  }, []);

  useEffect(() => {
    if (loading || helperScreen !== 'final') {
      return;
    }
    if (searchParams.get(INVENTORY_OPEN_EXPIRING_QUERY_PARAM) !== '1') {
      return;
    }
    setFinalReviewStatModalKind('expiring');
    const next = new URLSearchParams(searchParams);
    next.delete(INVENTORY_OPEN_EXPIRING_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [helperScreen, loading, searchParams, setSearchParams]);

  const handleCloseFinalReviewLocationPreview = useCallback(() => {
    setFinalReviewLocationPreview(null);
  }, []);

  const handleOpenFinalReviewLocationPreview = useCallback((locationName: string): void => {
    setFinalReviewLocationPreview(locationName);
  }, []);

  const handlePreviewLocationKeyDown = useCallback(
    (locationName: string) =>
      (e: KeyboardEvent<HTMLButtonElement>): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpenFinalReviewLocationPreview(locationName);
        }
      },
    [handleOpenFinalReviewLocationPreview],
  );

  const finalReviewPreviewTodayYmd = useMemo(
    () => getHelperTodayYmd(user?.settings?.timezone),
    [user?.settings?.timezone],
  );

  const finalReviewLocationPreviewData = useMemo(() => {
    if (!finalReviewLocationPreview) {
      return null;
    }
    const loc = finalReviewLocationPreview;
    const lm = isMultiLocationWizard ? lineMapsByLocation[loc] ?? {} : lineMap;
    const photos = isMultiLocationWizard ? photosByLocation[loc] ?? [] : uploadedStocktakeItems;
    const rowPhotoRefs: StocktakePhotoLinkRef[] = photos.map((p) => ({
      id: p.id,
      canonicalUrl: p.canonicalUrl,
      displayUrl: p.displayUrl,
      localPreviewUrl: p.localPreviewUrl,
      persistedPhotoRef: p.persistedPhotoRef,
    }));
    const rowsHere = filterStocktakeRowsForWizardLocation(
      stocktakeRowsOrdered,
      manualTemplateRows,
      manualItemIdSet,
      wizardLocations,
      loc,
    );
    const noSet = new Set(noItemsByLocation[loc] ?? []);
    const items = rowsHere
      .map((row): HomeStockLocationResultItem | null => {
        if (noSet.has(getRowCategory(row.itemId))) {
          return null;
        }
        const st = lm[row.itemId];
        if (!st || st.status === null) {
          return null;
        }
        return {
          itemKey: row.itemId,
          itemName: st.itemName,
          categoryName: getRowCategory(row.itemId).trim(),
          sortOrder: st.sortOrder,
          stockLevel: st.status,
          expiries: lineStateToPreviewExpiries(st),
          photoDisplayUrl: resolveLinePhotoDisplayUrl(rowPhotoRefs, st.existingPhotoUrl),
          persistedPhotoRef: (() => {
            const trimmedExisting = st.existingPhotoUrl.trim();
            const linked =
              trimmedExisting !== ''
                ? rowPhotoRefs.find((photo) => photo.canonicalUrl.trim() === trimmedExisting)
                : undefined;
            return linked?.persistedPhotoRef ?? rowPhotoRefs.find((photo) => photo.persistedPhotoRef)?.persistedPhotoRef;
          })(),
        };
      })
      .filter((item): item is HomeStockLocationResultItem => item !== null)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.itemName.localeCompare(b.itemName));
    const emptyCategories = orderEmptyCategoriesForPreview(noItemsByLocation[loc] ?? [], wizardCategories);
    return { locationName: loc, items, emptyCategories };
  }, [
    finalReviewLocationPreview,
    getRowCategory,
    isMultiLocationWizard,
    lineMap,
    lineMapsByLocation,
    manualItemIdSet,
    manualTemplateRows,
    noItemsByLocation,
    photosByLocation,
    stocktakeRowsOrdered,
    uploadedStocktakeItems,
    wizardCategories,
    wizardLocations,
  ]);

  const wizardTotalStocktakePhotosDeduped = useMemo(() => {
    const urls = new Set<string>();
    if (isMultiLocationWizard) {
      for (const loc of wizardLocations) {
        for (const p of photosByLocation[loc] ?? []) {
          const c = p.canonicalUrl.trim();
          if (c !== '') {
            urls.add(c);
          }
        }
      }
    } else {
      for (const p of uploadedStocktakeItems) {
        const c = p.canonicalUrl.trim();
        if (c !== '') {
          urls.add(c);
        }
      }
    }
    return urls.size;
  }, [isMultiLocationWizard, photosByLocation, uploadedStocktakeItems, wizardLocations]);

  const sortedMultiLocationModalEntries = useMemo(() => {
    if (!isMultiLocationWizard) {
      return [] as typeof multiLocationHubEntries;
    }
    const incomplete = multiLocationHubEntries.filter((e) => !e.readyForReview);
    const complete = multiLocationHubEntries.filter((e) => e.readyForReview);
    return [...incomplete, ...complete];
  }, [isMultiLocationWizard, multiLocationHubEntries]);

  const multiLocationModalIncomplete = useMemo(
    () => sortedMultiLocationModalEntries.filter((e) => !e.readyForReview),
    [sortedMultiLocationModalEntries],
  );
  const multiLocationModalComplete = useMemo(
    () => sortedMultiLocationModalEntries.filter((e) => e.readyForReview),
    [sortedMultiLocationModalEntries],
  );

  const handleDismissHelperLocationCoachmark = useCallback((): void => {
    if (instanceId) {
      sessionStorage.setItem(`fdh-inv-helper-loc-coach-${instanceId}`, '1');
    }
    setSelectLocationModalCoachmarkOpen(false);
  }, [instanceId]);

  const handleHelperLocationCoachmarkCloseKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleDismissHelperLocationCoachmark();
      }
    },
    [handleDismissHelperLocationCoachmark],
  );

  const handleCloseSelectLocationModal = useCallback((): void => {
    setSelectLocationModalOpen(false);
  }, []);

  const handleSelectLocationModalBackdropClick = useCallback((): void => {
    if (leaveHomeStockWizardConfirmOpen) {
      return;
    }
    handleCloseSelectLocationModal();
  }, [handleCloseSelectLocationModal, leaveHomeStockWizardConfirmOpen]);

  const handleSelectLocationModalBackdropKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key !== 'Escape') {
        return;
      }
      if (leaveHomeStockWizardConfirmOpen) {
        return;
      }
      handleCloseSelectLocationModal();
    },
    [handleCloseSelectLocationModal, leaveHomeStockWizardConfirmOpen],
  );

  const handleSelectLocationModalPanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        if (leaveHomeStockWizardConfirmOpen) {
          e.stopPropagation();
          return;
        }
        e.stopPropagation();
        handleCloseSelectLocationModal();
      }
    },
    [handleCloseSelectLocationModal, leaveHomeStockWizardConfirmOpen],
  );

  const handlePickLocationFromModal = useCallback(
    (index: number): void => {
      if (!isMultiLocationWizard || index < 0 || index >= wizardLocations.length) {
        return;
      }
      setLocationStepIndex(index);
      setError(null);
      const entry = multiLocationHubEntries[index];
      const openPhotosFirst =
        taskInstance?.status === APPROVAL_STATUS.NEEDS_UPDATE || !entry?.hasPhoto;
      setHelperScreen(openPhotosFirst ? 'photos' : 'itemEdit');
      setSelectLocationModalOpen(false);
    },
    [isMultiLocationWizard, multiLocationHubEntries, taskInstance?.status, wizardLocations.length],
  );

  const handleOpenLeaveHomeStockWizardConfirm = useCallback((): void => {
    setLeaveHomeStockWizardConfirmOpen(true);
  }, []);

  const handleCancelLeaveHomeStockWizardConfirm = useCallback((): void => {
    setLeaveHomeStockWizardConfirmOpen(false);
  }, []);

  const handleConfirmLeaveHomeStockWizard = useCallback((): void => {
    setLeaveHomeStockWizardConfirmOpen(false);
    setSelectLocationModalOpen(false);
    navigate('/today');
  }, [navigate]);

  const handleViewThisHomeStockTask = useCallback((): void => {
    if (!instanceId) {
      return;
    }
    setLeaveHomeStockWizardConfirmOpen(false);
    setSelectLocationModalOpen(false);
    navigate(buildHelperHomeStockOverviewPath(instanceId), {
      state: { helperHomeStockOverview: true } satisfies HelperHomeStockOverviewLocationState,
    });
  }, [instanceId, navigate]);

  const handleViewThisHomeStockTaskKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      handleViewThisHomeStockTask();
    },
    [handleViewThisHomeStockTask],
  );

  const showZeroPhotoWizardTaskActions = wizardTotalStocktakePhotosDeduped === 0;

  const handleGoFinalReviewFromSelectLocationModal = useCallback((): void => {
    if (wizardLocations.length === 0) {
      return;
    }
    const allReady =
      multiLocationHubEntries.length > 0 && multiLocationHubEntries.every((e) => e.readyForReview);
    if (!allReady) {
      return;
    }
    setError(null);
    setSelectLocationModalOpen(false);
    setHelperScreen('final');
  }, [multiLocationHubEntries, wizardLocations.length]);

  const handleLeaveHomeStockOpenKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenLeaveHomeStockWizardConfirm();
    }
  }, [handleOpenLeaveHomeStockWizardConfirm]);

  const handleFinalReviewFromModalKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleGoFinalReviewFromSelectLocationModal();
    }
  }, [handleGoFinalReviewFromSelectLocationModal]);

  const handleEditLocationFromReview = useCallback(
    (index: number): void => {
      handlePickLocationFromModal(index);
    },
    [handlePickLocationFromModal],
  );

  const computePendingEmptyCategoriesForLocation = useCallback(
    (locationName: string): string[] => {
      if (wizardCategories.length === 0) {
        return [];
      }
      const rowsHere = filterStocktakeRowsForWizardLocation(
        stocktakeRowsOrdered,
        manualTemplateRows,
        manualItemIdSet,
        wizardLocations,
        locationName,
      );
      const noSet = new Set(noItemsByLocation[locationName] ?? []);
      const pending: string[] = [];
      for (const cat of wizardCategories) {
        if (noSet.has(cat)) {
          continue;
        }
        const rowsInCat = rowsHere.filter((r) => getRowCategory(r.itemId) === cat);
        if (rowsInCat.length === 0) {
          pending.push(cat);
        }
      }
      return pending;
    },
    [
      getRowCategory,
      manualItemIdSet,
      manualTemplateRows,
      noItemsByLocation,
      stocktakeRowsOrdered,
      wizardCategories,
      wizardLocations,
    ],
  );

  const batchMarkNoItemsCategoriesForLocation = useCallback(
    (loc: string, categories: string[]): void => {
      if (loc === '' || categories.length === 0) {
        return;
      }
      setNoItemsByLocation((prev) => {
        const cur = new Set(prev[loc] ?? []);
        categories.forEach((c) => cur.add(c));
        return { ...prev, [loc]: Array.from(cur) };
      });
      const clearRowsForCategories = (lm: Record<string, LineState>): Record<string, LineState> => {
        const rowsHere = filterStocktakeRowsForWizardLocation(
          stocktakeRowsOrdered,
          manualTemplateRows,
          manualItemIdSet,
          wizardLocations,
          loc,
        );
        const catSet = new Set(categories);
        const next = { ...lm };
        for (const row of rowsHere) {
          const cat = getRowCategory(row.itemId);
          if (!catSet.has(cat)) {
            continue;
          }
          const st = next[row.itemId];
          if (!st) {
            continue;
          }
          next[row.itemId] = {
            ...st,
            status: null,
            existingPhotoUrl: '',
            everMatchedByAi: false,
            aiRaw: {},
            ...defaultLineExpiry(),
          };
        }
        return next;
      };
      if (isMultiLocationWizard) {
        setLineMapsByLocation((prev) => ({
          ...prev,
          [loc]: clearRowsForCategories(prev[loc] ?? {}),
        }));
      } else {
        setLineMap((prev) => clearRowsForCategories(prev));
      }
    },
    [
      getRowCategory,
      isMultiLocationWizard,
      manualItemIdSet,
      manualTemplateRows,
      stocktakeRowsOrdered,
      wizardLocations,
    ],
  );

  const handleManualRowCategoryChange = (itemId: string, nextCategory: string): void => {
    if (!manualItemIdSet.has(itemId)) {
      return;
    }
    const nextTrim = nextCategory.trim();
    if (wizardCategories.length > 0 && !wizardCategories.includes(nextTrim)) {
      return;
    }
    const prevCategory = getRowCategory(itemId).trim();
    if (prevCategory === nextTrim) {
      setCategoryEditingItemId(null);
      return;
    }
    setManualTemplateRows((prev) =>
      prev.map((row) => (row.itemId === itemId ? { ...row, categoryName: nextTrim } : row)),
    );
    setCategoryEditingItemId(null);

    const loc =
      wizardLocations.length > 1 ? wizardLocations[locationStepIndex] ?? '' : wizardLocations[0] ?? 'Default';
    if (loc === '') {
      return;
    }
    setNoItemsByLocation((prev) => {
      const cur = prev[loc] ?? [];
      const toRemove = new Set<string>();
      if (prevCategory !== '') {
        toRemove.add(prevCategory);
      }
      if (nextTrim !== '') {
        toRemove.add(nextTrim);
      }
      const filtered = cur.filter((c) => !toRemove.has(c));
      if (filtered.length === cur.length) {
        return prev;
      }
      return { ...prev, [loc]: filtered };
    });
  };

  const handleOpenRenameLineItemModal = (itemId: string): void => {
    const st = activeLineMap[itemId];
    if (!st) {
      return;
    }
    setRenameModalItemId(itemId);
    setRenameDraft(st.itemName);
  };

  const handleCancelRenameLineItemModal = (): void => {
    setRenameModalItemId(null);
    setRenameDraft('');
  };

  const handleConfirmRenameLineItemModal = (): void => {
    if (!renameModalItemId) {
      return;
    }
    const itemId = renameModalItemId;
    const st = activeLineMap[itemId];
    if (!st) {
      handleCancelRenameLineItemModal();
      return;
    }
    const trimmed = renameDraft.trim();
    if (trimmed === '' || trimmed === st.itemName) {
      handleCancelRenameLineItemModal();
      return;
    }
    if (manualItemIdSet.has(itemId)) {
      setManualTemplateRows((prev) =>
        prev.map((r) => (r.itemId === itemId ? { ...r, itemName: trimmed } : r)),
      );
    }
    if (isMultiLocationWizard) {
      const loc = currentLocationName;
      if (loc === '') {
        handleCancelRenameLineItemModal();
        return;
      }
      setLineMapsByLocation((prev) => {
        const cur = { ...(prev[loc] ?? {}) };
        const row = cur[itemId];
        if (!row) {
          return prev;
        }
        cur[itemId] = {
          ...row,
          itemName: trimmed,
          itemNameEmployerDisplay: undefined,
          itemNameHelperSnapshot: undefined,
        };
        return { ...prev, [loc]: cur };
      });
    } else {
      setLineMap((prev) => {
        const row = prev[itemId];
        if (!row) {
          return prev;
        }
        return { ...prev, [itemId]: { ...row, itemName: trimmed, itemNameEmployerDisplay: undefined, itemNameHelperSnapshot: undefined } };
      });
    }
    handleCancelRenameLineItemModal();
  };

  const handleToggleCategoryEditRow = (itemId: string): void => {
    setCategoryEditingItemId((cur) => (cur === itemId ? null : itemId));
  };

  const handleContinueFromPhotosStep = (): void => {
    const photos = isMultiLocationWizard
      ? photosByLocation[wizardLocations[locationStepIndex] ?? ''] ?? []
      : uploadedStocktakeItems;
    const hasPhoto = photos.some((p) => p.canonicalUrl.trim() !== '');
    if (!hasPhoto) {
      setError(t('inventory.needAtLeastOneBatchPhoto'));
      return;
    }
    setError(null);
    setHelperScreen('itemEdit');
  };

  const completeItemEditContinueAfterCategoriesOk = (): void => {
    setError(null);
    if (isMultiLocationWizard) {
      if (multiLocationReadyCount >= wizardLocations.length && wizardLocations.length > 0) {
        setHelperScreen('final');
      } else {
        setSelectLocationModalOpen(true);
      }
      return;
    }
    if (wizardCategories.length === 0) {
      for (const row of stocktakeRowsOrdered) {
        const st = lineMap[row.itemId];
        if (st && st.status === null) {
          setError(t('inventory.stockLevelRequired'));
          return;
        }
      }
    }
    setHelperScreen('final');
  };

  useEffect(() => {
    if (!resumeAfterNoItemsConfirmRef.current) {
      return;
    }
    resumeAfterNoItemsConfirmRef.current = false;
    completeItemEditContinueAfterCategoriesOk();
  }, [noItemsByLocation, lineMapsByLocation, lineMap]);

  const handleToggleNoItemsConfirmCheckbox = (category: string, checked: boolean): void => {
    setNoItemsConfirmChecked((prev) => ({ ...prev, [category]: checked }));
  };

  const handleCancelNoItemsConfirmModal = (): void => {
    setNoItemsConfirmModalOpen(false);
    setNoItemsConfirmCategories([]);
    setNoItemsConfirmChecked({});
  };

  const handleConfirmNoItemsConfirmModal = (): void => {
    const loc = noItemsConfirmLocationRef.current;
    const pending = noItemsConfirmCategories;
    if (loc === '' || pending.length === 0) {
      handleCancelNoItemsConfirmModal();
      return;
    }
    if (!pending.every((c) => noItemsConfirmChecked[c])) {
      return;
    }
    batchMarkNoItemsCategoriesForLocation(loc, pending);
    setNoItemsConfirmModalOpen(false);
    setNoItemsConfirmCategories([]);
    setNoItemsConfirmChecked({});
    resumeAfterNoItemsConfirmRef.current = true;
  };

  const handleContinueFromItemEditStep = (): void => {
    const loc = isMultiLocationWizard ? wizardLocations[locationStepIndex] ?? '' : wizardLocations[0] ?? 'Default';
    const lm = isMultiLocationWizard ? lineMapsByLocation[loc] ?? {} : lineMap;

    const deferredErr = validateCategoriesForLocation(loc, lm, {
      deferEmptyWithoutNoItemsTick: true,
    });
    if (deferredErr) {
      setError(deferredErr);
      return;
    }

    const pending = computePendingEmptyCategoriesForLocation(loc);
    if (pending.length > 0) {
      noItemsConfirmLocationRef.current = loc;
      setNoItemsConfirmCategories(pending);
      const initial: Record<string, boolean> = {};
      pending.forEach((c) => {
        initial[c] = false;
      });
      setNoItemsConfirmChecked(initial);
      setNoItemsConfirmModalOpen(true);
      return;
    }

    const fullErr = validateCategoriesForLocation(loc, lm);
    if (fullErr) {
      setError(fullErr);
      return;
    }

    completeItemEditContinueAfterCategoriesOk();
  };

  const handleBackFromWizard = (): void => {
    if (helperScreen === 'final') {
      setError(null);
      if (isMultiLocationWizard) {
        setSelectLocationModalOpen(true);
        return;
      }
      setHelperScreen('itemEdit');
      return;
    }
    if (helperScreen === 'itemEdit') {
      setHelperScreen('photos');
      setError(null);
      return;
    }
    if (helperScreen === 'photos') {
      if (isMultiLocationWizard) {
        setSelectLocationModalOpen(true);
      } else {
        handleOpenLeaveHomeStockWizardConfirm();
      }
      setError(null);
      return;
    }
  };

  const performInventorySubmit = useCallback(async () => {
    if (!instanceId || !taskInstance) {
      return;
    }
    if (isMultiLocationWizard) {
      for (const loc of wizardLocations) {
        const lm = lineMapsByLocation[loc] ?? {};
        const photos = photosByLocation[loc] ?? [];
        const rowsHereSubmit = filterStocktakeRowsForWizardLocation(
          stocktakeRowsOrdered,
          manualTemplateRows,
          manualItemIdSet,
          wizardLocations,
          loc,
        );
        for (const row of rowsHereSubmit) {
          if (!lm[row.itemId]) {
            setError(t('task.complete.submitError'));
            return;
          }
        }
        const hasPhoto = photos.some((p) => p.canonicalUrl.trim() !== '');
        if (!hasPhoto) {
          setError(t('inventory.wizard.photoRequiredPerLocation'));
          return;
        }
        const catErr = validateCategoriesForLocation(loc, lm);
        if (catErr) {
          setError(catErr);
          return;
        }
      }
    } else {
      for (const row of stocktakeRowsOrdered) {
        if (!lineMap[row.itemId]) {
          setError(t('task.complete.submitError'));
          return;
        }
      }
      const sharedPhotoUrl = resolveStocktakeSharedPhotoUrl({
        rowsForPhotoFallback: stocktakeRowsOrdered,
        lineMap,
        uploadedStocktakeItems,
      });
      if (sharedPhotoUrl === '') {
        setError(t('inventory.needAtLeastOneBatchPhoto'));
        return;
      }
      const singleLoc = wizardLocations[0] ?? 'Default';
      const singleExp = validateCategoriesForLocation(singleLoc, lineMap);
      if (singleExp) {
        setError(singleExp);
        return;
      }
      if (wizardCategories.length === 0) {
        for (const row of stocktakeRowsOrdered) {
          const st = lineMap[row.itemId];
          if (st && st.status === null) {
            setError(t('inventory.stockLevelRequired'));
            return;
          }
        }
      }
    }
    if (!completedDate || !completedTime) {
      setError(t('task.complete.completedAtRequired'));
      return;
    }
    const completedAt = `${completedDate} ${completedTime}`;
    setSubmitting(true);
    setError(null);
    try {
      const buildItemsForLocation = (loc: string, lm: Record<string, LineState>, locPhotos: StocktakeUploadedItem[]) => {
        const rowsPayload = filterStocktakeRowsForWizardLocation(
          stocktakeRowsOrdered,
          manualTemplateRows,
          manualItemIdSet,
          wizardLocations,
          loc,
        );
        const sharedPhotoUrl = resolveStocktakeSharedPhotoUrl({
          rowsForPhotoFallback: rowsPayload,
          lineMap: lm,
          uploadedStocktakeItems: locPhotos,
        });
        const out: Array<{
          itemName: string;
          categoryName: string;
          stockLevel: InventoryLevel;
          photoUrl: string;
          aiRaw: Record<string, unknown>;
          expiries: Array<{ expiryDate: string }>;
          sortOrder: number;
        }> = [];
        const noSet = new Set(noItemsByLocation[loc] ?? []);
        for (const row of rowsPayload) {
          if (noSet.has(getRowCategory(row.itemId))) {
            continue;
          }
          const st = lm[row.itemId];
          if (!st || st.status === null) {
            continue;
          }
          const own = st.existingPhotoUrl?.trim() ?? '';
          const url = (own !== '' ? own : sharedPhotoUrl).trim();
          const ownAiRaw: Record<string, unknown> = st.aiRaw ?? {};
          const aiRaw =
            own !== ''
              ? ownAiRaw
              : { ...ownAiRaw, sharedPhotoInferred: true, sharedFromBatchUrl: sharedPhotoUrl };
          out.push({
            itemName: st.itemName,
            categoryName: getRowCategory(row.itemId),
            stockLevel: st.status,
            photoUrl: url,
            aiRaw,
            expiries: buildExpiriesPayload(st),
            sortOrder: row.sortOrder,
            ...(st.itemNameHelperSnapshot?.trim() && st.itemNameEmployerDisplay?.trim()
              ? {
                  itemNameEmployerDisplay: st.itemNameEmployerDisplay.trim(),
                  itemNameHelperSnapshot: st.itemNameHelperSnapshot.trim(),
                }
              : {}),
          });
        }
        return out;
      };

      const body = isMultiLocationWizard
        ? {
            locations: wizardLocations.map((loc, idx) => {
              const lm = lineMapsByLocation[loc] ?? {};
              const locPhotos = photosByLocation[loc] ?? [];
              const photoUrls = Array.from(
                new Set(locPhotos.map((item) => item.canonicalUrl.trim()).filter((url) => url !== '')),
              );
              const locationPhotos = locPhotos
                .filter((p) => p.canonicalUrl.trim() !== '')
                .map((p) => ({
                  url: p.canonicalUrl.trim(),
                  aiAnalysisCompleted: p.aiAnalysisCompleted,
                }));
              return {
                locationName: loc,
                photoUrls,
                locationPhotos,
                items: buildItemsForLocation(loc, lm, locPhotos),
                noItemsCategories: [...(noItemsByLocation[loc] ?? [])],
                sortOrder: idx,
              };
            }),
            note: '',
            completedAt,
          }
        : (() => {
            const items = buildItemsForLocation(
              wizardLocations[0] ?? 'Default',
              lineMap,
              uploadedStocktakeItems,
            );
            const locationName = wizardLocations[0] ?? 'Default';
            const photoUrls = Array.from(
              new Set(
                uploadedStocktakeItems.map((item) => item.canonicalUrl.trim()).filter((url) => url !== ''),
              ),
            );
            const locationPhotos = uploadedStocktakeItems
              .filter((p) => p.canonicalUrl.trim() !== '')
              .map((p) => ({
                url: p.canonicalUrl.trim(),
                aiAnalysisCompleted: p.aiAnalysisCompleted,
              }));
            return {
              locations: [
                {
                  locationName,
                  photoUrls,
                  locationPhotos,
                  items,
                  noItemsCategories: [...(noItemsByLocation[locationName] ?? [])],
                  sortOrder: 0,
                },
              ],
              note: '',
              completedAt,
            };
          })();

      const isResubmit = taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE;
      const res = isResubmit
        ? await inventoryApi.resubmit(instanceId, body)
        : await inventoryApi.submit(instanceId, body);
      if (res.success) {
        showSuccessToast(isResubmit ? t('task.resubmit.submitSuccess') : t('toast.task.completeSuccess'));
        navigate('/today', { replace: true });
        return;
      }
      setError(res.error ?? t('task.complete.submitError'));
    } catch (caught) {
      if (isLimitReachedError(caught)) {
        return;
      }
      setError(
        caught instanceof ApiClientError
          ? mapApiClientErrorToMessage(caught, t) ?? t('task.complete.submitError')
          : t('task.complete.submitError'),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    completedDate,
    completedTime,
    getRowCategory,
    instanceId,
    isMultiLocationWizard,
    lineMap,
    lineMapsByLocation,
    navigate,
    noItemsByLocation,
    photosByLocation,
    showSuccessToast,
    t,
    taskInstance,
    stocktakeRowsOrdered,
    uploadedStocktakeItems,
    validateCategoriesForLocation,
    wizardLocations,
    manualTemplateRows,
    manualItemIdSet,
  ]);

  const requestInventorySubmit = useCallback(() => {
    if (!taskInstance || !user) {
      return;
    }
    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    if (
      taskInstance.dueDate > todayYmd &&
      isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
    ) {
      setSubmitConfirmModalOpen(true);
      return;
    }
    void performInventorySubmit();
  }, [performInventorySubmit, taskInstance, user]);

  const handleCloseSubmitConfirmModal = () => {
    if (submitting) {
      return;
    }
    setSubmitConfirmModalOpen(false);
  };

  const handleConfirmSubmitModal = () => {
    setSubmitConfirmModalOpen(false);
    void performInventorySubmit();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!instanceId || !taskInstance) {
      return;
    }
    if (taskInstance.inventoryChainBlockedReason) {
      setError(t('inventory.chainBlockedBanner'));
      return;
    }
    if (helperScreen === 'photos') {
      handleContinueFromPhotosStep();
      return;
    }
    if (helperScreen === 'itemEdit') {
      handleContinueFromItemEditStep();
      return;
    }
    if (helperScreen === 'final') {
      if (!completedDate || !completedTime) {
        setError(t('task.complete.completedAtRequired'));
        return;
      }
      if (commentThreadRef.current?.hasUnsentDraft()) {
        setUnsentCommentModalOpen(true);
        return;
      }
      requestInventorySubmit();
    }
  };

  const handleUnsentCommentBackToEdit = () => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentModalOpen(false);
    commentThreadRef.current?.openComposer();
  };

  const handleUnsentCommentSubmitOnly = () => {
    if (unsentCommentDialogBusy) {
      return;
    }
    commentThreadRef.current?.clearDraft();
    setUnsentCommentModalOpen(false);
    requestInventorySubmit();
  };

  const handleUnsentCommentSubmitWithComment = async () => {
    if (unsentCommentDialogBusy) {
      return;
    }
    setUnsentCommentDialogBusy(true);
    const sent = await commentThreadRef.current?.sendDraft();
    setUnsentCommentDialogBusy(false);
    if (sent === false) {
      return;
    }
    setUnsentCommentModalOpen(false);
    requestInventorySubmit();
  };

  const handleBack = () => {
    navigateBack(navigate, '/today');
  };

  if (!user || user.role !== USER_ROLE.HELPER) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <p className="text-center text-gray-600 dark:text-gray-300" role="alert">
          {t('inventory.helperOnly')}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400"
          aria-hidden
        />
      </div>
    );
  }

  if (error && !taskInstance) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <p className="text-center text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 text-primary-600 underline hover:no-underline focus:outline-none dark:text-primary-400"
        >
          {t('task.action.back')}
        </button>
      </div>
    );
  }

  const canUseListlessWizardShell =
    taskInstance != null &&
    !taskInstance.task.inventoryListId?.trim() &&
    wizardCategories.length > 0;

  if (
    !taskInstance ||
    (templateOrdered.length === 0 &&
      !(taskInstance.task.taskKind === TASK_KIND.INVENTORY && canUseListlessWizardShell))
  ) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <p className="text-center text-amber-800 dark:text-amber-200" role="alert">
          {t('inventory.emptyTemplate')}
        </p>
        <button
          type="button"
          onClick={handleBack}
          className="mt-4 text-primary-600 underline focus:outline-none dark:text-primary-400"
        >
          {t('task.action.back')}
        </button>
      </div>
    );
  }

  const isResubmit = taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE;
  const isChainBlocked = Boolean(taskInstance.inventoryChainBlockedReason);

  const helperScreenTitle =
    helperScreen === 'photos'
      ? t('inventory.wizard.photosStepTitle')
      : helperScreen === 'itemEdit'
        ? t('inventory.wizard.reviewItemsTitle')
        : t('inventory.wizard.finalReviewTitle');
  const activeWizardLocationKey = isMultiLocationWizard
    ? currentLocationName
    : wizardLocations[0] ?? '';
  const activeWizardLocationLabel = resolveWizardLocationLabel(
    activeWizardLocationKey.trim() !== '' ? activeWizardLocationKey : taskInstance.task.title,
  );
  const helperScreenSubtitle =
    helperScreen === 'photos'
      ? t('inventory.wizard.photosStepSubtitle', {
          location: activeWizardLocationLabel,
        })
      : helperScreen === 'itemEdit'
        ? t('inventory.wizard.reviewItemsSubtitle')
        : t('inventory.wizard.finalReviewSubtitle');

  const helperTodayYmdForBanner = getHelperTodayYmd(user?.settings?.timezone);
  const futureDueBannerMessage = getFutureDueBannerMessage(
    USER_ROLE.HELPER,
    helperTodayYmdForBanner,
    taskInstance.dueDate,
    t,
  );

  const renderZeroPhotoWizardLeaveRow = (): ReactNode => {
    if (!showZeroPhotoWizardTaskActions) {
      return (
        <AppButton
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          tabIndex={0}
          aria-label={t('inventory.wizard.leaveHomeStockWorkButton')}
          onClick={handleOpenLeaveHomeStockWizardConfirm}
          onKeyDown={handleLeaveHomeStockOpenKeyDown}
          disabled={submitting || bulkAnalyzing || isChainBlocked}
        >
          {t('inventory.wizard.leaveHomeStockWorkButton')}
        </AppButton>
      );
    }
    return (
      <>
        <AppButton
          type="button"
          variant="ghost"
          size="lg"
          fullWidth
          tabIndex={0}
          aria-label={t('inventory.wizard.viewThisTaskButton')}
          onClick={handleViewThisHomeStockTask}
          onKeyDown={handleViewThisHomeStockTaskKeyDown}
          disabled={submitting || bulkAnalyzing || isChainBlocked}
        >
          {t('inventory.wizard.viewThisTaskButton')}
        </AppButton>
        <AppButton
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          tabIndex={0}
          aria-label={t('inventory.wizard.leaveHomeStockWorkButton')}
          onClick={handleOpenLeaveHomeStockWizardConfirm}
          onKeyDown={handleLeaveHomeStockOpenKeyDown}
          disabled={submitting || bulkAnalyzing || isChainBlocked}
        >
          {t('inventory.wizard.leaveHomeStockWorkButton')}
        </AppButton>
      </>
    );
  };

  return (
    <div className="min-h-0 bg-[#F7F9FC] dark:bg-gray-950">
      <form
        onSubmit={handleFormSubmit}
        className="mx-auto max-w-lg space-y-4 px-4 pb-28 pt-3"
      >
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          capture="environment"
          className="sr-only"
          aria-hidden
          tabIndex={-1}
          onChange={handleStocktakeFileChange}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple
          className="sr-only"
          aria-hidden
          tabIndex={-1}
          onChange={handleStocktakeFileChange}
        />
        <div className="flex flex-col gap-3 border-b border-gray-200/70 pb-4 dark:border-gray-700/70">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleBackFromWizard}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
              aria-label={t('task.action.back')}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span
              className="rounded-full bg-gray-100 px-3 py-1.5 text-[13px] font-bold uppercase tracking-wide text-gray-700 dark:bg-gray-800 dark:text-gray-200"
              aria-label={t('inventory.wizard.stepBadgeWithSubstepAria', {
                major: majorStepDisplay,
                sub: stepSubSuffix,
                total: totalMajorWizardSteps,
              })}
            >
              {t('inventory.wizard.stepBadgeWithSubstep', {
                major: majorStepDisplay,
                sub: stepSubSuffix,
                total: totalMajorWizardSteps,
              })}
            </span>
            <div className="h-10 w-10 shrink-0" aria-hidden />
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-primary-100/60"
            aria-label={t('inventory.wizard.progressAria', { percent: helperFlowProgressPercent })}
          >
            <div
              className="h-full rounded-full bg-primary-600 transition-all duration-500 ease-out"
              style={{ width: `${helperFlowProgressPercent}%` }}
            />
          </div>
          {futureDueBannerMessage ? (
            <div
              className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-200"
              role="status"
              aria-live="polite"
              aria-label={futureDueBannerMessage}
            >
              <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-sm font-medium leading-snug">{futureDueBannerMessage}</span>
            </div>
          ) : null}
          <div className="flex flex-wrap items-start gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
              {helperScreenTitle}
            </h1>
            {taskInstance && taskInstanceHasHelperTranslations(taskInstance) ? (
              <AutoTranslationHint
                targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                  t,
                  taskInstance.task.translation?.targetLanguage,
                  language,
                )}
                showingOriginal={showTaskContentOriginal}
                onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                className="mt-1"
              />
            ) : null}
            {helperScreen === 'photos' || helperScreen === 'itemEdit' ? (() => {
              const locLabel = isMultiLocationWizard
                ? resolveWizardLocationLabel(currentLocationName)
                : resolveWizardLocationLabel(wizardLocations[0] ?? 'Default');
              if (locLabel.trim() === '') {
                return null;
              }
              return (
                <span
                  className="mt-1 rounded-full bg-primary-100/50 px-3 py-1 text-xs font-bold text-primary-600 dark:bg-emerald-950/50 dark:text-emerald-300"
                  aria-label={t('inventory.wizard.reviewLocationBadgeAria', { location: locLabel })}
                >
                  {locLabel}
                </span>
              );
            })() : null}
          </div>
          <p className="text-[15px] font-medium leading-relaxed text-gray-500 dark:text-gray-400">
            {helperScreenSubtitle}
          </p>
        </div>

        {helperScreen === 'photos' || helperScreen === 'itemEdit' ? (
          <div
            className={
              isResubmit
                ? 'rounded-2xl border border-orange-200/80 bg-orange-50/90 p-4 dark:border-orange-800/50 dark:bg-orange-950/30'
                : 'rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900'
            }
          >
            <h2
              className={
                isResubmit
                  ? 'font-semibold text-gray-900 dark:text-orange-100'
                  : 'font-semibold text-gray-900 dark:text-gray-100'
              }
            >
              {resolveTaskDisplayTitle(taskInstance.task, showTaskContentOriginal)}
            </h2>
            {taskInstance.task.description ? (
              <p
                className={
                  isResubmit
                    ? 'mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-orange-200/90'
                    : 'mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300'
                }
              >
                {resolveTaskDisplayDescription(taskInstance.task, showTaskContentOriginal)}
              </p>
            ) : null}
            <div
              className={
                isResubmit
                  ? 'mt-3 space-y-1 text-xs text-gray-500 dark:text-orange-200/80'
                  : 'mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400'
              }
            >
              <p>
                {t('task.review.taskDate')}: {taskInstance.dueDate}
              </p>
              {taskInstance.task.dueTime ? (
                <p>
                  {t('task.form.dueTimeLabel')}: {taskInstance.task.dueTime}
                </p>
              ) : null}
              {helperScreen === 'photos' && wizardCategories.length > 0 ? (
                <PressableHintProvider>
                  <div
                    className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5"
                    role="group"
                    aria-label={`${t('task.todo.inventoryPreviewCategoriesLabel')}: ${wizardCategories.map(resolveWizardCategoryLabel).join(', ')}`}
                  >
                    <span className="w-full shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('task.todo.inventoryPreviewCategoriesLabel')}
                    </span>
                    {wizardCategories.map((category, categoryIndex) => (
                      <InventoryCategoryBadge
                        key={category}
                        category={category}
                        displayCategory={resolveWizardCategoryLabel(category)}
                        badgeIndexMap={wizardCategoryBadgeIndexMap}
                        hintId={`wizard-preview-cat-${categoryIndex}`}
                      />
                    ))}
                  </div>
                </PressableHintProvider>
              ) : null}
            </div>
            {taskInstance.needsUpdateReason && String(taskInstance.needsUpdateReason).trim() !== '' ? (
              <div
                className="mt-3 rounded-xl border border-red-200/80 bg-white/90 p-3 dark:border-red-900/50 dark:bg-red-950/25"
                role="region"
                aria-label={t('task.today.employerFeedbackLabel')}
              >
                <p className="text-xs font-semibold text-red-800 dark:text-red-200">
                  {t('task.today.employerFeedbackLabel')}
                </p>
                <p className="mt-1 text-sm text-red-900 dark:text-red-100">
                  {resolveInstanceDisplayNeedsUpdateReason(
                    taskInstance.needsUpdateReason,
                    taskInstance.translation,
                    showTaskContentOriginal,
                  )}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {isChainBlocked ? (
          <div
            className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-red-700 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
            aria-live="polite"
            aria-label={t('inventory.chainBlockedBanner')}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
              />
            </svg>
            <span className="text-sm font-medium">{t('inventory.chainBlockedBanner')}</span>
          </div>
        ) : null}

        {helperScreen === 'final' ? (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFinalReviewStatModalKind('lowStock')}
                className="relative flex flex-col items-center gap-1 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-white via-amber-50/80 to-orange-100/70 p-3 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 dark:border-amber-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-amber-950/35 dark:to-gray-900"
                aria-label={t('inventory.statDetailModal.openLowStockAria', {
                  count: finalReviewStats.lowStock,
                })}
              >
                <InventorySummaryStatCardChevron />
                <div className="rounded-lg bg-gray-100 p-1.5 text-amber-500 dark:bg-gray-800">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-50">{finalReviewStats.lowStock}</span>
                <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                  {t('task.review.inventoryStatLowStockCaption')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setFinalReviewStatModalKind('expiring')}
                className="relative flex flex-col items-center gap-1 rounded-2xl border border-rose-200/80 bg-gradient-to-br from-white via-rose-50/80 to-red-100/70 p-3 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 dark:border-rose-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-rose-950/35 dark:to-gray-900"
                aria-label={t('inventory.statDetailModal.openExpiringAria', {
                  count: finalReviewStats.expiringSoon,
                })}
              >
                <InventorySummaryStatCardChevron />
                <div className="rounded-lg bg-gray-100 p-1.5 text-red-500 dark:bg-gray-800">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-50">{finalReviewStats.expiringSoon}</span>
                <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                  {t('task.review.inventoryStatExpiringCaption')}
                </span>
              </button>
              <div
                role="group"
                aria-label={`${t('task.review.inventoryStatTotalPhotosCaption')}: ${wizardTotalStocktakePhotosDeduped}`}
                className="flex flex-col items-center gap-1 rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-white via-emerald-50/80 to-emerald-100/80 p-3 text-center shadow-sm dark:border-emerald-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-emerald-950/35 dark:to-gray-900"
              >
                <div className="rounded-lg bg-gray-100 p-1.5 text-primary-600 dark:bg-gray-800">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-50">
                  {wizardTotalStocktakePhotosDeduped}
                </span>
                <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                  {t('task.review.inventoryStatTotalPhotosCaption')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="px-1 text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                {t('inventory.wizard.locationDetailsSection')}
              </span>
              <div className="overflow-hidden rounded-3xl border border-gray-200/60 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-900">
                <PressableHintProvider>
                <ul className="list-none divide-y-2 divide-gray-200 p-0 dark:divide-gray-600" role="list">
                  {(isMultiLocationWizard ? wizardLocations : [wizardLocations[0] ?? 'Default']).map((loc, locIdx) => {
                    const lm = isMultiLocationWizard ? lineMapsByLocation[loc] ?? {} : lineMap;
                    const photos = isMultiLocationWizard ? photosByLocation[loc] ?? [] : uploadedStocktakeItems;
                    const rowsForReviewLoc = filterStocktakeRowsForWizardLocation(
                      stocktakeRowsOrdered,
                      manualTemplateRows,
                      manualItemIdSet,
                      wizardLocations,
                      loc,
                    );
                    const itemCount = rowsForReviewLoc.filter((row) => {
                      if (isRowSkippedForNoItems(loc, row.itemId)) {
                        return false;
                      }
                      const st = lm[row.itemId];
                      return Boolean(st && st.status !== null);
                    }).length;
                    const emptyCatCount = (noItemsByLocation[loc] ?? []).length;
                    const photoCount = photos.filter((p) => p.canonicalUrl.trim() !== '').length;
                    const emptyCatCaptionFull = t('inventory.wizard.reviewLocationStatEmptyCatCaptionFull');
                    const idx = isMultiLocationWizard ? locIdx : 0;
                    const hubEntryForFinalLoc = isMultiLocationWizard
                      ? multiLocationHubEntries.find((e) => e.locationName === loc)
                      : undefined;
                    const finalReviewLocationIconReady =
                      !isMultiLocationWizard || Boolean(hubEntryForFinalLoc?.readyForReview);
                    const locDisplayLabel = resolveWizardLocationLabel(loc);
                    return (
                      <li key={loc} className="flex items-center justify-between gap-3 p-5" role="listitem">
                        <div className="flex min-w-0 flex-1 items-center gap-4">
                          <div
                            className={[
                              'shrink-0 self-start rounded-xl p-3',
                              finalReviewLocationIconReady
                                ? 'bg-primary-100 text-primary-600'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                            ].join(' ')}
                            aria-hidden
                          >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                              />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">
                              {locDisplayLabel}
                            </p>
                            <div className="mt-2 grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700">
                              <div className="flex flex-col items-center px-1">
                                <span className="text-base font-bold leading-none text-gray-900 dark:text-gray-50">
                                  {itemCount}
                                </span>
                                <span className="mt-1 text-[10px] font-medium uppercase leading-none tracking-tight text-gray-500 dark:text-gray-400">
                                  {t('inventory.wizard.reviewLocationStatItemsCaption')}
                                </span>
                              </div>
                              <div className="flex flex-col items-center px-1">
                                <span className="text-base font-bold leading-none text-gray-900 dark:text-gray-50">
                                  {emptyCatCount}
                                </span>
                                <PressableHint
                                  hintId={`final-loc-empty-cat-${locIdx}`}
                                  detail={emptyCatCaptionFull}
                                  ariaLabel={emptyCatCaptionFull}
                                  placement="top"
                                  triggerClassName="mt-1 cursor-help text-[10px] font-medium uppercase leading-none tracking-tight text-gray-500 dark:text-gray-400"
                                >
                                  {t('inventory.wizard.reviewLocationStatEmptyCatCaption')}
                                </PressableHint>
                              </div>
                              <div className="flex flex-col items-center px-1">
                                <span className="text-base font-bold leading-none text-gray-900 dark:text-gray-50">
                                  {photoCount}
                                </span>
                                <span className="mt-1 text-[10px] font-medium uppercase leading-none tracking-tight text-gray-500 dark:text-gray-400">
                                  {t('inventory.wizard.reviewLocationStatPhotosCaption')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-center gap-1 self-center">
                          {isMultiLocationWizard ? (
                            <button
                              type="button"
                              onClick={() => handleEditLocationFromReview(idx)}
                              disabled={submitting || bulkAnalyzing || isChainBlocked}
                              className="rounded-lg bg-orange-50 p-2 text-orange-600 transition-colors hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-400/40 disabled:opacity-50 dark:bg-orange-950/40 dark:text-orange-300 dark:hover:bg-orange-950/60"
                              aria-label={t('inventory.wizard.editLocationFromReviewAria', { name: loc })}
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            tabIndex={0}
                            onClick={() => handleOpenFinalReviewLocationPreview(loc)}
                            onKeyDown={handlePreviewLocationKeyDown(loc)}
                            disabled={submitting || bulkAnalyzing || isChainBlocked}
                            className="rounded-lg bg-blue-50 p-2 text-blue-600 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400/40 disabled:opacity-50 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                            aria-label={t('inventory.wizard.previewLocationResultsAria', { name: loc })}
                          >
                            <HomeStockPreviewDocumentIcon />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </PressableHintProvider>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="px-1 text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                {t('inventory.wizard.submissionInfoSection')}
              </span>
              <div className="rounded-3xl border border-gray-200/60 bg-white p-5 shadow-sm dark:border-gray-600 dark:bg-gray-900">
                <div className="flex flex-col gap-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('task.complete.completedAtLabel')}
                  </label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <IsoDatePicker
                      id="inv-final-completed-date"
                      ariaLabel={t('task.complete.completedAtLabel')}
                      value={completedDate}
                      onChange={setCompletedDate}
                      maxDate={getTodayDate()}
                      disabled={submitting}
                      valueFormat="iso"
                    />
                    <NativeTimeInput
                      id="inv-final-completed-time"
                      aria-label={t('task.complete.completedAtLabel')}
                      value={completedTime}
                      onChange={(e) => setCompletedTime(e.target.value)}
                      className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      disabled={submitting}
                    />
                  </div>
                  <div className="h-px w-full bg-gray-200/80 dark:bg-gray-700" />
                  {instanceId ? (
                    <CommentThread
                      ref={commentThreadRef}
                      targetType={COMMENT_TARGET_TYPE.TASK_INSTANCE}
                      targetId={instanceId}
                    />
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-3xl border border-primary-600/20 bg-primary-600/5 p-5 dark:border-emerald-900/50 dark:bg-emerald-950/25">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg shadow-primary-600/25">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 dark:text-gray-50">{t('inventory.wizard.readyToSubmitTitle')}</p>
                <p className="mt-1 text-xs font-medium leading-relaxed text-gray-600 dark:text-gray-400">
                  {t('inventory.wizard.readyToSubmitDisclaimer')}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {helperScreen === 'photos' || helperScreen === 'itemEdit' ? (
          <>
        {helperScreen === 'photos' ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('inventory.stocktakePanelTitle')}</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('inventory.stocktakePanelHint')}</p>
          <div
            className={`mt-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/40 ${
              bulkAnalyzing ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="flex w-full items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleCameraPickClick}
                  disabled={bulkAnalyzing || isChainBlocked}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-primary-900/40 dark:text-primary-300 dark:hover:bg-primary-900/60"
                  aria-label={t('task.complete.takePhoto')}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleGalleryPickClick}
                  disabled={bulkAnalyzing || isChainBlocked}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  aria-label={t('task.complete.choosePhoto')}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
              {!bulkAnalyzing ? (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">{t('inventory.stocktakeMaxFilesHint')}</p>
              ) : null}
            </div>
          </div>
          {pageRateInfo ? (
            <p className="mt-2 text-sm text-amber-800 dark:text-amber-200" role="status">
              {pageRateInfo}
            </p>
          ) : null}
          {bulkError ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
              {bulkError}
            </p>
          ) : null}
          {activeUploadedStocktakeItems.length > 0 ? (
            <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-600">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                {t('inventory.uploadedStocktakePhotosHeading')}
              </p>
              <ul
                className="mt-2 flex max-h-48 list-none flex-wrap gap-3 overflow-y-auto p-0"
                role="list"
                aria-label={t('inventory.uploadedStocktakePhotosHeading')}
              >
                {activeUploadedStocktakeItems.map((item, index) => (
                  <li key={item.id} role="listitem">
                    <StocktakeUploadedPhotoThumb
                      displayUrl={resolveStocktakeItemPreviewSrc(item)}
                      authedApiPath={
                        !item.localPreviewUrl && item.persistedPhotoRef && instanceId
                          ? buildInventoryPersistedPhotoApiPath(instanceId, item.persistedPhotoRef)
                          : undefined
                      }
                      imageAlt={t('inventory.uploadedStocktakePhotoAlt', { n: index + 1 })}
                      enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                      closeViewerLabel={t('common.closeViewer')}
                      removeAriaLabel={t('inventory.removePhotoAria')}
                      onRequestRemove={() => handleRequestRemoveStocktakeImage(item)}
                      actionsDisabled={bulkAnalyzing || submitting || isChainBlocked}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {stocktakeRowsForActiveWizardUi.length > 0 && !hasActiveStocktakePhotos && helperScreen === 'photos' ? (
            <p
              className="mt-3 rounded-xl bg-gray-100 px-3 py-2 text-xs text-gray-700 dark:bg-gray-800/60 dark:text-gray-300"
              role="status"
            >
              {t('inventory.needPhotoBeforeStockLevels')}
            </p>
          ) : null}
        </div>
        ) : null}

        {showInventoryLineCards ? (
          <PressableHintProvider>
          <>
        {stocktakeRowsForActiveWizardUi.map((row) => {
          const st = activeLineMap[row.itemId];
          if (!st) return null;
          const isManualRow = manualItemIdSet.has(row.itemId);
          const skippedRaw =
            wizardCategories.length > 0 &&
            activeLocationKeyForNoItems !== '' &&
            isRowSkippedForNoItems(activeLocationKeyForNoItems, row.itemId);
          const skipped = skippedRaw && !isManualRow;
          const manualNoItemsCategoryClash = skippedRaw && isManualRow;
          const rowPhotoRefs: StocktakePhotoLinkRef[] = activeUploadedStocktakeItems.map((p) => ({
            id: p.id,
            canonicalUrl: p.canonicalUrl,
            displayUrl: p.displayUrl,
            localPreviewUrl: p.localPreviewUrl,
            persistedPhotoRef: p.persistedPhotoRef,
          }));
          const linkedRowPhoto =
            findStocktakePhotoByCanonical(rowPhotoRefs, st.existingPhotoUrl) ?? rowPhotoRefs[0];
          const rowItemPhotoDisplayUrl = resolveLinePhotoDisplayUrl(rowPhotoRefs, st.existingPhotoUrl);
          const rowItemPhotoApiPath =
            linkedRowPhoto?.localPreviewUrl?.trim()
              ? undefined
              : resolveInventoryPersistedPhotoAuthedApiPath(instanceId, rowPhotoRefs, st.existingPhotoUrl);
          const rowItemPhotoId = resolveLineSelectedPhotoId(rowPhotoRefs, st.existingPhotoUrl);
          const showRowPhotoPicker = !skipped && rowPhotoRefs.length > 1;
          return (
            <div
              key={row.itemId}
              className={
                skipped
                  ? 'rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-5 dark:border-gray-600 dark:bg-gray-800/40'
                  : 'rounded-2xl border-2 border-gray-200/60 bg-white p-5 shadow-sm dark:border-gray-600 dark:bg-gray-900'
              }
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-50">{st.itemName}</h3>
                    <button
                      type="button"
                      onClick={() => handleOpenRenameLineItemModal(row.itemId)}
                      disabled={submitting || bulkAnalyzing || isChainBlocked}
                      className="rounded-md bg-primary-100/30 p-1 text-primary-600 transition-colors hover:bg-primary-100/60 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-50 dark:bg-emerald-950/40 dark:text-emerald-300"
                      aria-label={t('inventory.wizard.renameItemAria')}
                    >
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                  </div>
                  {(() => {
                    const rowCat = getRowCategory(row.itemId);
                    const showCat = rowCat.trim() !== '' && rowCat !== 'Uncategorized';
                    const manualCatEditable = manualItemIdSet.has(row.itemId) && wizardCategories.length > 0;
                    if (!showCat && !manualCatEditable) {
                      return null;
                    }
                    const editingCat = categoryEditingItemId === row.itemId;
                    return (
                      <div className="mt-2 flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {showCat ? (
                            <InventoryCategoryBadge
                              category={rowCat}
                              displayCategory={resolveWizardCategoryLabel(rowCat)}
                              badgeIndexMap={wizardCategoryBadgeIndexMap}
                              hintId={`wizard-row-cat-${row.itemId}`}
                            />
                          ) : null}
                          {manualCatEditable ? (
                            <button
                              type="button"
                              onClick={() => handleToggleCategoryEditRow(row.itemId)}
                              disabled={submitting || bulkAnalyzing || isChainBlocked}
                              className="rounded-md p-1 text-primary-600 transition-colors hover:bg-primary-100/40 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:opacity-50"
                              aria-label={t('inventory.wizard.editCategoryAria')}
                              aria-expanded={editingCat}
                            >
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                          ) : null}
                        </div>
                        {manualCatEditable && editingCat ? (
                          <>
                            <label className="sr-only" htmlFor={`inv-row-cat-${row.itemId}`}>
                              {t('inventory.manualAdd.rowCategoryEditLabel')}
                            </label>
                            <select
                              id={`inv-row-cat-${row.itemId}`}
                              value={getRowCategory(row.itemId)}
                              onChange={(e) => handleManualRowCategoryChange(row.itemId, e.target.value)}
                              disabled={submitting || bulkAnalyzing || isChainBlocked}
                              autoFocus
                              className="min-h-10 w-full max-w-md rounded-lg border-2 border-amber-200/90 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-300/80 dark:border-amber-700/60 dark:bg-gray-800 dark:text-gray-100"
                              aria-label={t('inventory.manualAdd.rowCategoryEditLabel')}
                            >
                              {wizardCategories.map((category) => (
                                <option key={category} value={category}>
                                  {resolveWizardCategoryLabel(category)}
                                </option>
                              ))}
                            </select>
                          </>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex shrink-0 items-start gap-2">
                  {!skipped && (rowItemPhotoDisplayUrl.trim() !== '' || rowItemPhotoApiPath) ? (
                    <StocktakeUploadedPhotoThumb
                      displayUrl={rowItemPhotoDisplayUrl}
                      authedApiPath={rowItemPhotoApiPath}
                      imageAlt={t('task.review.inventoryItemPhotoAria')}
                      enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                      closeViewerLabel={t('common.closeViewer')}
                      removeAriaLabel=""
                      onRequestRemove={() => undefined}
                      actionsDisabled
                      thumbClassName="h-20 w-20"
                    />
                  ) : null}
                  {manualItemIdSet.has(row.itemId) ? (
                    <button
                      type="button"
                      onClick={() => handleRequestRemoveManualRow(row.itemId)}
                      disabled={submitting || bulkAnalyzing || isChainBlocked}
                      className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 dark:text-gray-500 dark:hover:text-red-400"
                      aria-label={t('inventory.manualAdd.removeAria', { name: st.itemName })}
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
              {skipped ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                  {t('inventory.wizard.rowSkippedNoItemsCategory')}
                </p>
              ) : null}
              {manualNoItemsCategoryClash ? (
                <div
                  className="mt-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100"
                  role="status"
                >
                  {t('inventory.wizard.manualItemNoItemsCategoryBanner')}
                </div>
              ) : null}
              {!skipped && st.everMatchedByAi ? (
                <p className="text-xs text-green-700 dark:text-green-300" role="status">
                  {t('inventory.matchedByAiHint')}
                </p>
              ) : null}
              {showRowPhotoPicker ? (
                <div className="mb-4">
                  <InventoryStocktakePhotoRadioPicker
                    groupName={`inv-item-photo-${row.itemId}`}
                    photos={rowPhotoRefs.map((p) => ({
                      id: p.id,
                      displayUrl: resolveStocktakePhotoLinkPreviewSrc(p),
                      authedApiPath:
                        !p.localPreviewUrl && p.persistedPhotoRef && instanceId
                          ? buildInventoryPersistedPhotoApiPath(instanceId, p.persistedPhotoRef)
                          : undefined,
                    }))}
                    selectedPhotoId={rowItemPhotoId}
                    onSelectPhotoId={(photoId) => handleSetLineItemPhoto(row.itemId, photoId)}
                    label={t('inventory.itemPhotoLabel')}
                    disabled={submitting || bulkAnalyzing || isChainBlocked}
                    t={t}
                  />
                </div>
              ) : null}
              {!skipped ? (
                <>
                  <div className="mb-4 flex flex-col gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('inventory.stockLevel')}
                    </span>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label={t('inventory.stockLevel')}>
                      {LEVELS.map((lev) => (
                        <button
                          key={lev}
                          type="button"
                          onClick={() => handleSetStatus(row.itemId, lev)}
                          className={
                            st.status === lev
                              ? 'rounded-lg border-2 border-primary-600 bg-primary-600 py-2 text-xs font-bold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40'
                              : 'rounded-lg border-2 border-gray-200 bg-gray-100 py-2 text-xs font-bold text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }
                          aria-pressed={st.status === lev}
                        >
                          {t(`inventory.level.${lev}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  {st.status !== null ? (
                    <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 dark:border-gray-700" role="group" aria-label={t('inventory.expiry.sectionAria')}>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t('inventory.expiry.sectionTitle')}
                      </span>
                      <div className="mt-1 flex flex-col gap-3">
                        {st.useNoExpiry === null ? (
                          <p className="text-xs text-amber-800 dark:text-amber-200" role="status">
                            {t('inventory.expiry.chooseModeHint')}
                          </p>
                        ) : null}
                        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="radio"
                            name={`inv-expiry-mode-${row.itemId}`}
                            checked={st.useNoExpiry === true}
                            onChange={() => handleSetUseNoExpiry(row.itemId, true)}
                            disabled={submitting || bulkAnalyzing || isChainBlocked}
                            className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                            aria-label={t('inventory.expiry.noExpiryAria', { item: st.itemName })}
                          />
                          <span>{t('inventory.expiry.noExpiryLabel')}</span>
                        </label>
                        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="radio"
                            name={`inv-expiry-mode-${row.itemId}`}
                            checked={st.useNoExpiry === false}
                            onChange={() => handleSetUseNoExpiry(row.itemId, false)}
                            disabled={submitting || bulkAnalyzing || isChainBlocked}
                            className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                            aria-label={t('inventory.expiry.hasDatesAria', { item: st.itemName })}
                          />
                          <span>{t('inventory.expiry.hasDatesLabel')}</span>
                        </label>
                        {st.useNoExpiry === true ? (
                          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-800/50">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {t('inventory.expiry.noExpiryLabel')}
                            </span>
                            <CalendarIcon className="h-4 w-4 shrink-0" />
                          </div>
                        ) : null}
                        {st.useNoExpiry === false ? (
                          <div className="flex flex-col gap-2">
                            {st.expiryYmdList.map((ymd, expIdx) => (
                              <div
                                key={`${row.itemId}-exp-${expIdx}`}
                                className="flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-800/50"
                              >
                                <div className="min-w-0 flex-1">
                                  <IsoDatePicker
                                    id={`inv-expiry-${row.itemId}-${expIdx}`}
                                    ariaLabel={t('inventory.expiry.datePickerAria', { item: st.itemName, n: expIdx + 1 })}
                                    value={ymd}
                                    onChange={(next) => handleExpiryYmdChange(row.itemId, expIdx, next)}
                                    disabled={submitting || bulkAnalyzing || isChainBlocked}
                                    minDate={expiryPickerDateBounds.minDate}
                                    maxDate={expiryPickerDateBounds.maxDate}
                                    captionLayout="dropdown"
                                    valueFormat="iso"
                                  />
                                </div>
                                {st.expiryYmdList.length > 1 ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExpiryRow(row.itemId, expIdx)}
                                    disabled={submitting || bulkAnalyzing || isChainBlocked}
                                    className="min-h-9 shrink-0 self-center rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                    aria-label={t('inventory.expiry.removeDateAria', { item: st.itemName, n: expIdx + 1 })}
                                  >
                                    {t('inventory.expiry.removeDate')}
                                  </button>
                                ) : null}
                              </div>
                            ))}
                            {st.expiryYmdList.length < maxExpiryDatesPerItem ? (
                              <button
                                type="button"
                                onClick={() => handleAddExpiryRow(row.itemId)}
                                disabled={submitting || bulkAnalyzing || isChainBlocked}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary-100/60 bg-primary-100/20 py-2 text-xs font-bold text-primary-600 transition-colors hover:bg-primary-100/35 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:opacity-60 dark:border-emerald-600/70 dark:bg-emerald-950/45 dark:text-emerald-100 dark:hover:bg-emerald-900/55 dark:focus:ring-emerald-400/35"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                {t('inventory.expiry.addDate')}
                              </button>
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{t('inventory.expiry.maxDatesHint')}</p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
        {notRecognizedByAi.length > 0 ? (
          <div
            className="rounded-2xl border border-amber-200/80 bg-amber-50/90 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <p className="font-medium">{t('inventory.notRecognizedByAi')}</p>
            <p className="mt-2 text-amber-800/90 dark:text-amber-200/90">{t('inventory.unmatchedUseSharedPhotoHint')}</p>
            <ul className="mt-1 list-inside list-disc">
              {notRecognizedByAi.map((row) => (
                <li key={row.itemId}>{row.itemName}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div
          className={
            manualAddSectionExpanded
              ? 'rounded-2xl border-2 border-dashed border-gray-200 bg-white/80 p-5 shadow-sm dark:border-gray-600 dark:bg-gray-900/50'
              : 'rounded-2xl border border-gray-200 bg-white/80 px-4 py-3 shadow-sm dark:border-gray-600 dark:bg-gray-900/50'
          }
          role="region"
          aria-label={t('inventory.manualAdd.sectionAria')}
        >
          <button
            type="button"
            id="inv-manual-add-toggle"
            aria-expanded={manualAddSectionExpanded}
            aria-controls="inv-manual-add-panel"
            aria-label={
              manualAddSectionExpanded
                ? t('inventory.manualAdd.collapseToggleAria')
                : t('inventory.manualAdd.expandToggleAria')
            }
            onClick={handleToggleManualAddSectionExpand}
            className={`flex w-full min-h-11 items-center gap-2 rounded-xl px-1 text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:text-gray-300 dark:hover:bg-gray-800/60 ${
              manualAddSectionExpanded ? 'mb-3' : ''
            }`}
          >
            <svg className="h-6 w-6 shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span
              id="inv-manual-add-heading"
              className="flex-1 text-center text-base font-bold text-gray-900 dark:text-gray-100"
            >
              {t('inventory.wizard.addItemManuallyTitle')}
            </span>
            <svg
              className={`h-5 w-5 shrink-0 text-gray-500 transition-transform dark:text-gray-400 ${
                manualAddSectionExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {manualAddSectionExpanded ? (
          <div id="inv-manual-add-panel" className="flex flex-col gap-3" aria-labelledby="inv-manual-add-heading">
            {wizardCategories.length > 0 ? (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-manual-cat-select">
                  {t('inventory.manualAdd.categoryLabel')}
                </label>
                <select
                  id="inv-manual-cat-select"
                  value={manualAddCategorySelect}
                  onChange={(e) => setManualAddCategorySelect(e.target.value)}
                  disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                  className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  aria-label={t('inventory.manualAdd.categoryLabel')}
                  aria-required="true"
                >
                  <option value="">{t('inventory.manualAdd.categoryPlaceholder')}</option>
                  {wizardCategories.map((category) => (
                    <option key={category} value={category}>
                      {resolveWizardCategoryLabel(category)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-manual-cat-text">
                  {t('inventory.manualAdd.categoryOtherLabel')}
                </label>
                <input
                  id="inv-manual-cat-text"
                  type="text"
                  maxLength={120}
                  value={manualAddCategoryOther}
                  onChange={(e) => setManualAddCategoryOther(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddManualInventoryRow();
                    }
                  }}
                  disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                  className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('inventory.manualAdd.categoryOtherPlaceholder')}
                  aria-label={t('inventory.manualAdd.categoryOtherLabel')}
                  aria-required="true"
                  autoComplete="off"
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-200" htmlFor="inv-manual-item-name">
                {t('inventory.manualAdd.itemNameLabel')}
              </label>
              <input
                id="inv-manual-item-name"
                type="text"
                maxLength={200}
                value={manualAddItemName}
                onChange={(e) => setManualAddItemName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddManualInventoryRow();
                  }
                }}
                disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder={t('inventory.manualAdd.itemNamePlaceholder')}
                aria-label={t('inventory.manualAdd.itemNameLabel')}
                aria-required="true"
                autoComplete="off"
              />
            </div>
            {(() => {
              const manualPhotos = isMultiLocationWizard
                ? photosByLocation[wizardLocations[locationStepIndex] ?? ''] ?? []
                : uploadedStocktakeItems;
              if (manualPhotos.length <= 1) {
                return null;
              }
              return (
                <InventoryStocktakePhotoRadioPicker
                  groupName="inv-manual-add-photo"
                  photos={manualPhotos.map((p) => ({
                    id: p.id,
                    displayUrl: resolveStocktakeItemPreviewSrc(p),
                    authedApiPath:
                      !p.localPreviewUrl && p.persistedPhotoRef && instanceId
                        ? buildInventoryPersistedPhotoApiPath(instanceId, p.persistedPhotoRef)
                        : undefined,
                  }))}
                  selectedPhotoId={manualAddPhotoId}
                  onSelectPhotoId={setManualAddPhotoId}
                  label={t('inventory.itemPhotoLabel')}
                  disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                  t={t}
                />
              );
            })()}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('inventory.stockLevel')}
              </span>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label={t('inventory.stockLevel')}>
                {LEVELS.map((lev) => (
                  <button
                    key={lev}
                    type="button"
                    onClick={() => setManualAddStatus(lev)}
                    disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                    className={
                      manualAddStatus === lev
                        ? 'rounded-lg border-2 border-primary-600 bg-primary-600 py-2 text-xs font-bold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40'
                        : 'rounded-lg border-2 border-gray-200 bg-gray-100 py-2 text-xs font-bold text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }
                    aria-pressed={manualAddStatus === lev}
                  >
                    {t(`inventory.level.${lev}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2" role="group" aria-label={t('inventory.expiry.sectionAria')}>
              <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('inventory.expiry.sectionTitle')}
              </span>
              {manualAddUseNoExpiry === null ? (
                <p className="text-xs text-amber-800 dark:text-amber-200" role="status">
                  {t('inventory.expiry.chooseModeHint')}
                </p>
              ) : null}
              <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="radio"
                  name="inv-manual-add-expiry-mode"
                  checked={manualAddUseNoExpiry === true}
                  onChange={() => {
                    setManualAddUseNoExpiry(true);
                    setManualAddExpiryYmdList([]);
                  }}
                  disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                  className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  aria-label={t('inventory.expiry.noExpiryAria', { item: t('inventory.wizard.addItemManuallyTitle') })}
                />
                <span>{t('inventory.expiry.noExpiryLabel')}</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="radio"
                  name="inv-manual-add-expiry-mode"
                  checked={manualAddUseNoExpiry === false}
                  onChange={() => {
                    setManualAddUseNoExpiry(false);
                    setManualAddExpiryYmdList((p) => (p.length > 0 ? p : ['']));
                  }}
                  disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                  className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  aria-label={t('inventory.expiry.hasDatesAria', { item: t('inventory.wizard.addItemManuallyTitle') })}
                />
                <span>{t('inventory.expiry.hasDatesLabel')}</span>
              </label>
              {manualAddUseNoExpiry === true ? (
                <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-800/50">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('inventory.expiry.noExpiryLabel')}
                  </span>
                  <CalendarIcon className="h-4 w-4 shrink-0" />
                </div>
              ) : null}
              {manualAddUseNoExpiry === false ? (
                <div className="flex flex-col gap-2">
                  {manualAddExpiryYmdList.map((ymd, expIdx) => (
                    <div
                      key={`manual-exp-${expIdx}`}
                      className="flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-600 dark:bg-gray-800/50"
                    >
                      <div className="min-w-0 flex-1">
                        <IsoDatePicker
                          id={`inv-manual-expiry-${expIdx}`}
                          ariaLabel={t('inventory.expiry.datePickerAria', {
                            item: t('inventory.manualAdd.itemNameLabel'),
                            n: expIdx + 1,
                          })}
                          value={ymd}
                          onChange={(next) => {
                            setManualAddExpiryYmdList((prev) => prev.map((d, i) => (i === expIdx ? next : d)));
                          }}
                          disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                          minDate={expiryPickerDateBounds.minDate}
                          maxDate={expiryPickerDateBounds.maxDate}
                          captionLayout="dropdown"
                          valueFormat="iso"
                        />
                      </div>
                      {manualAddExpiryYmdList.length > 1 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setManualAddExpiryYmdList((prev) => {
                              const next = prev.filter((_, i) => i !== expIdx);
                              return next.length > 0 ? next : [''];
                            })
                          }
                          disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                          className="min-h-9 shrink-0 self-center rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                          aria-label={t('inventory.expiry.removeDateAria', {
                            item: t('inventory.manualAdd.itemNameLabel'),
                            n: expIdx + 1,
                          })}
                        >
                          {t('inventory.expiry.removeDate')}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {manualAddExpiryYmdList.length < maxExpiryDatesPerItem ? (
                    <button
                      type="button"
                      onClick={() =>
                        setManualAddExpiryYmdList((prev) =>
                          prev.length >= maxExpiryDatesPerItem ? prev : [...prev, ''],
                        )
                      }
                      disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary-100/60 bg-primary-100/20 py-2 text-xs font-bold text-primary-600 transition-colors hover:bg-primary-100/35 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:opacity-60 dark:border-emerald-600/70 dark:bg-emerald-950/45 dark:text-emerald-100 dark:hover:bg-emerald-900/55 dark:focus:ring-emerald-400/35"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('inventory.expiry.addDate')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleAddManualInventoryRow}
              disabled={submitting || bulkAnalyzing || isChainBlocked || manualAddBusy}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-primary-600/30 bg-primary-100/25 py-3 text-sm font-bold text-primary-600 transition-colors hover:bg-primary-100/40 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-60 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {manualAddBusy ? t('inventory.manualAdd.adding') : t('inventory.manualAdd.addButton')}
            </button>
          </div>
          ) : null}
        </div>
          </>
          </PressableHintProvider>
        ) : null}

        </>
        ) : null}

        {error ? (
          <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-200" role="alert">
            {error}
          </div>
        ) : null}

        {bulkAnalyzing ? (
          <div
            className={`fixed inset-0 z-50 flex flex-col items-center justify-center overflow-y-auto p-6 ${MODAL_BACKDROP_SCRIM_CLASS}`}
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-live="polite"
            aria-label={
              bulkBusyMode === 'upload'
                ? t('inventory.stocktakeUploading')
                : t('receipt.analyze.analyzing')
            }
          >
            <div
              className={`w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-gray-900 dark:ring-1 dark:ring-gray-700 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
            >
              <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {bulkBusyMode === 'upload'
                  ? t('inventory.stocktakeUploading')
                  : t('receipt.analyze.analyzing')}
              </p>
              {bulkProgress.total > 1 ? (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {bulkBusyMode === 'upload'
                    ? t('inventory.stocktakeUploadingProgress', {
                        current: bulkProgress.current,
                        total: bulkProgress.total,
                      })
                    : t('receipt.analyze.analyzingProgress', {
                        current: bulkProgress.current,
                        total: Math.max(bulkProgress.total, 1),
                      })}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <InventoryDeleteStocktakePhotoDialog
          open={removeConfirmItem !== null}
          t={t}
          linkedItemCount={removePhotoLinkedItemCount}
          onCancel={handleCancelRemoveStocktakePhotoDialog}
          onKeepPhotoAndItems={handleKeepPhotoAndItems}
          onDeletePhotoAndItems={handleDeletePhotoAndLinkedItems}
        />

        <InventoryStocktakeAiSelectModal
          open={aiPhotoSelectModalOpen}
          photos={getActiveLocationPhotos().map((photo) => ({
            id: photo.id,
            displayUrl: resolveStocktakeItemPreviewSrc(photo),
            canonicalUrl: photo.canonicalUrl,
            aiAnalysisCompleted: photo.aiAnalysisCompleted,
            authedApiPath:
              !photo.localPreviewUrl && photo.persistedPhotoRef && instanceId
                ? buildInventoryPersistedPhotoApiPath(instanceId, photo.persistedPhotoRef)
                : undefined,
          }))}
          t={t}
          busy={bulkAnalyzing}
          onClose={() => setAiPhotoSelectModalOpen(false)}
          onConfirm={handleAiPhotoSelectConfirm}
        />

        <InventoryReanalyzePhotoConfirmDialog
          open={reanalyzeConfirmOpen}
          photoCount={pendingAiPhotoIds.length}
          itemCount={photosNeedingReanalyzeConfirm(pendingAiPhotoIds).itemCount}
          t={t}
          onCancel={() => {
            setReanalyzeConfirmOpen(false);
            setPendingAiPhotoIds([]);
          }}
          onConfirm={handleReanalyzeConfirmProceed}
        />

        <ConfirmDialog
          open={removeManualItemId !== null}
          title={t('inventory.wizard.removeManualItemTitle')}
          description={t('inventory.wizard.removeManualItemDescription', {
            name:
              removeManualItemId !== null
                ? activeLineMap[removeManualItemId]?.itemName ?? removeManualItemId
                : '',
          })}
          confirmLabel={t('inventory.manualAdd.remove')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleConfirmRemoveManualRow}
          onCancel={handleCancelRemoveManualRow}
          variant="danger"
        />

        <ConfirmDialog
          open={noItemsConfirmModalOpen}
          title={t('inventory.wizard.noItemsConfirmModalTitle')}
          description={t('inventory.wizard.noItemsConfirmModalDescription')}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleConfirmNoItemsConfirmModal}
          onCancel={handleCancelNoItemsConfirmModal}
          confirmDisabled={
            noItemsConfirmCategories.length === 0 ||
            !noItemsConfirmCategories.every((c) => Boolean(noItemsConfirmChecked[c]))
          }
        >
          <ul className="mt-4 list-none space-y-3 p-0" role="list" aria-label={t('inventory.wizard.noItemsConfirmModalTitle')}>
            {noItemsConfirmCategories.map((category) => (
              <li key={category} role="listitem">
                <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(noItemsConfirmChecked[category])}
                    onChange={(e) => handleToggleNoItemsConfirmCheckbox(category, e.target.checked)}
                    disabled={submitting || bulkAnalyzing || isChainBlocked}
                    className="mt-1 h-4 w-4 rounded border-gray-500 text-amber-500 focus:ring-amber-400"
                    aria-label={t('inventory.wizard.noItemCategoryAria', {
                      category: resolveWizardCategoryLabel(category),
                    })}
                  />
                  <span>
                    {t('inventory.wizard.noItemCategoryLabel', {
                      category: resolveWizardCategoryLabel(category),
                    })}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </ConfirmDialog>

        {renameModalItemId !== null ? (
          <div
            className={`${MODAL_BACKDROP_CENTER_CLASS} z-[60] ${MODAL_BACKDROP_SCRIM_CLASS}`}
            role="presentation"
            onClick={handleCancelRenameLineItemModal}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="inv-helper-rename-item-title"
              className={`relative z-10 w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                id="inv-helper-rename-item-title"
                className="text-lg font-bold text-gray-900 dark:text-gray-100"
              >
                {t('task.inventory.renameItemModalTitle')}
              </h2>
              <label
                className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300"
                htmlFor="inv-helper-rename-item-input"
              >
                {t('inventory.manualAdd.itemNameLabel')}
              </label>
              <input
                id="inv-helper-rename-item-input"
                type="text"
                maxLength={200}
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmRenameLineItemModal();
                  }
                }}
                className="mt-1 min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                autoComplete="off"
                autoFocus
                aria-label={t('inventory.manualAdd.itemNameLabel')}
              />
              <div className="mt-4 flex justify-end gap-2">
                <AppButton type="button" variant="secondary" onClick={handleCancelRenameLineItemModal}>
                  {t('common.cancel')}
                </AppButton>
                <AppButton type="button" variant="primary" onClick={handleConfirmRenameLineItemModal}>
                  {t('common.confirm')}
                </AppButton>
              </div>
            </div>
          </div>
        ) : null}

        <TaskSubmitConfirmModal
          open={submitConfirmModalOpen}
          dueDateYmd={taskInstance?.dueDate ?? ''}
          todayYmd={getHelperTodayYmd(user?.settings?.timezone)}
          t={t}
          title={t('task.submitConfirmTitle')}
          prompt={t('task.submitConfirmPrompt')}
          confirmLabel={t('task.submitConfirmAction')}
          cancelLabel={t('common.cancel')}
          submitting={submitting}
          onClose={handleCloseSubmitConfirmModal}
          onConfirm={handleConfirmSubmitModal}
        />

        <UnsentCommentOnSubmitDialog
          open={unsentCommentModalOpen}
          context="stockTake"
          busy={unsentCommentDialogBusy}
          onBackToEdit={handleUnsentCommentBackToEdit}
          onSubmitOnly={handleUnsentCommentSubmitOnly}
          onSubmitWithComment={handleUnsentCommentSubmitWithComment}
        />

        {selectLocationModalOpen && isMultiLocationWizard ? (
          <div
            role="presentation"
            className={`${MODAL_BACKDROP_CENTER_CLASS} z-[100] ${MODAL_BACKDROP_SCRIM_CLASS}`}
            onClick={handleSelectLocationModalBackdropClick}
            onKeyDown={handleSelectLocationModalBackdropKeyDown}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="inv-helper-select-loc-title"
              tabIndex={0}
              className={`relative z-10 w-full max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900 ${MODAL_PANEL_SCROLL_DVH_CLASS}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleSelectLocationModalPanelKeyDown}
            >
              <ModalCircleCloseButton
                className="absolute right-3 top-3"
                ariaLabel={t('inventory.wizard.selectLocationModalCloseAria')}
                onClick={handleCloseSelectLocationModal}
              />
              <div className="flex flex-wrap items-start gap-2 pr-10">
                <h2
                  id="inv-helper-select-loc-title"
                  className="text-lg font-bold text-gray-900 dark:text-gray-50"
                >
                  {t('inventory.wizard.selectLocationTitle')}
                </h2>
                {taskInstance && taskInstanceHasHelperTranslations(taskInstance) ? (
                  <AutoTranslationHint
                    targetLanguageLabel={resolveTranslationTargetLanguageLabel(
                      t,
                      taskInstance.task.translation?.targetLanguage,
                      language,
                    )}
                    showingOriginal={showTaskContentOriginal}
                    onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                    className="mt-0.5"
                  />
                ) : null}
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('inventory.wizard.selectLocationSubtitle')}
              </p>
              <div className="mt-4 rounded-2xl border border-primary-600/20 bg-primary-600/5 p-3 dark:border-emerald-800/40 dark:bg-emerald-950/20">
                <div className="flex items-center gap-2 text-xs font-bold text-primary-600 dark:text-emerald-400">
                  <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    {t('inventory.wizard.hubLocationsCompleted', {
                      done: multiLocationReadyCount,
                      total: wizardLocations.length,
                    })}
                  </span>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-4">
                {multiLocationModalIncomplete.length > 0 ? (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      {t('inventory.wizard.selectLocationIncompleteSection')}
                    </p>
                    <div className="mt-2 flex flex-col gap-3" role="list">
                      {multiLocationModalIncomplete.map((entry, entryIdx) => {
                        const isReady = entry.readyForReview;
                        const locationDisplayLabel = resolveWizardLocationLabel(entry.locationName);
                        const showCoachmark =
                          entryIdx === 0 && selectLocationModalCoachmarkOpen && !isReady;
                        return (
                          <div key={entry.locationName} className="relative">
                            {showCoachmark ? (
                              <div
                                className="pointer-events-none absolute bottom-full left-0 right-0 z-[25] mb-2 flex justify-center px-1"
                                role="dialog"
                                aria-modal="false"
                                aria-labelledby="inv-helper-loc-coach-desc"
                              >
                                <div className="pointer-events-auto relative w-full max-w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white px-3 pb-3 pt-2 shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:shadow-black/40">
                                  <button
                                    type="button"
                                    tabIndex={0}
                                    aria-label={t('task.review.inventoryLocationsCoachmarkDismissAria')}
                                    onClick={handleDismissHelperLocationCoachmark}
                                    onKeyDown={handleHelperLocationCoachmarkCloseKeyDown}
                                    className="absolute right-1.5 top-1.5 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                                  >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                  <p
                                    id="inv-helper-loc-coach-desc"
                                    className="pr-9 text-left text-sm leading-snug text-gray-900 dark:text-gray-50"
                                  >
                                    {t('inventory.wizard.tapToStart')}
                                  </p>
                                  <div
                                    className="pointer-events-none absolute left-1/2 top-full flex -translate-x-1/2 flex-col items-center text-white dark:text-gray-900"
                                    aria-hidden
                                  >
                                    <svg width="22" height="11" viewBox="0 0 22 11" className="drop-shadow-sm">
                                      <path fill="currentColor" d="M11 11 L1 0 H21 Z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ) : null}
                            <button
                              type="button"
                              role="listitem"
                              onClick={() => handlePickLocationFromModal(entry.index)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handlePickLocationFromModal(entry.index);
                                }
                              }}
                              disabled={submitting || bulkAnalyzing || isChainBlocked}
                              className={[
                                'flex w-full items-center justify-between rounded-2xl border-2 p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:opacity-60',
                                isReady
                                  ? 'border-primary-100 bg-primary-100/10 dark:border-emerald-900/50 dark:bg-emerald-950/20'
                                  : 'border-gray-200 bg-white shadow-sm hover:border-primary-600/40 dark:border-gray-600 dark:bg-gray-900',
                              ].join(' ')}
                              aria-label={t('inventory.wizard.locationHubSelectAria', {
                                name: locationDisplayLabel,
                              })}
                            >
                              <div className="flex items-center gap-4">
                                <div
                                  className={[
                                    'rounded-xl p-3',
                                    isReady
                                      ? 'bg-primary-100 text-primary-600'
                                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                                  ].join(' ')}
                                  aria-hidden
                                >
                                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                    {locationDisplayLabel}
                                  </h3>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t(entry.statusLineKey)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center">
                                {isReady ? (
                                  <svg
                                    className="h-6 w-6 text-primary-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="h-5 w-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                )}
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {multiLocationModalComplete.length > 0 ? (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      {t('inventory.wizard.selectLocationCompleteSection')}
                    </p>
                    <div className="mt-2 flex flex-col gap-3" role="list">
                      {multiLocationModalComplete.map((entry) => {
                        const isReady = entry.readyForReview;
                        const locationDisplayLabel = resolveWizardLocationLabel(entry.locationName);
                        return (
                          <button
                            key={entry.locationName}
                            type="button"
                            role="listitem"
                            onClick={() => handlePickLocationFromModal(entry.index)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handlePickLocationFromModal(entry.index);
                              }
                            }}
                            disabled={submitting || bulkAnalyzing || isChainBlocked}
                            className={[
                              'flex w-full items-center justify-between rounded-2xl border-2 p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:opacity-60',
                              isReady
                                ? 'border-primary-100 bg-primary-100/10 dark:border-emerald-900/50 dark:bg-emerald-950/20'
                                : 'border-gray-200 bg-white shadow-sm hover:border-primary-600/40 dark:border-gray-600 dark:bg-gray-900',
                            ].join(' ')}
                            aria-label={t('inventory.wizard.locationHubSelectAria', {
                              name: locationDisplayLabel,
                            })}
                          >
                            <div className="flex items-center gap-4">
                              <div
                                className={[
                                  'rounded-xl p-3',
                                  isReady
                                    ? 'bg-primary-100 text-primary-600'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                                ].join(' ')}
                                aria-hidden
                              >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                  {locationDisplayLabel}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {t(entry.statusLineKey)}
                                </p>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center">
                              {isReady ? (
                                <svg
                                  className="h-6 w-6 text-primary-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="h-5 w-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div className="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
                  {wizardLocations.length > 0 &&
                  multiLocationReadyCount >= wizardLocations.length &&
                  multiLocationHubEntries.length > 0 &&
                  multiLocationHubEntries.every((entry) => entry.readyForReview) ? (
                    <AppButton
                      type="button"
                      variant="primary"
                      size="lg"
                      fullWidth
                      tabIndex={0}
                      aria-label={t('inventory.wizard.selectLocationFinalReviewButton')}
                      onClick={handleGoFinalReviewFromSelectLocationModal}
                      onKeyDown={handleFinalReviewFromModalKeyDown}
                      disabled={submitting || bulkAnalyzing || isChainBlocked}
                    >
                      {t('inventory.wizard.selectLocationFinalReviewButton')}
                    </AppButton>
                  ) : null}
                  {renderZeroPhotoWizardLeaveRow()}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <ConfirmDialog
          open={leaveHomeStockWizardConfirmOpen}
          title={t('inventory.wizard.leaveHomeStockWorkConfirmTitle')}
          description={t('inventory.wizard.leaveHomeStockWorkConfirmDescription')}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          backdropZClass="z-[110]"
          variant="danger"
          onConfirm={handleConfirmLeaveHomeStockWizard}
          onCancel={handleCancelLeaveHomeStockWizardConfirm}
        />

        {helperScreen === 'photos' || helperScreen === 'itemEdit' ? (
          <div className="flex flex-col gap-3">
            {helperScreen === 'photos' ? (
              <>
                <AppButton
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={submitting || bulkAnalyzing || isChainBlocked}
                  onClick={handleOpenAiAnalyzeFlow}
                >
                  {t('inventory.wizard.continueWithAiAnalyze')}
                </AppButton>
                <AppButton
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  disabled={submitting || bulkAnalyzing || isChainBlocked}
                  onClick={handleContinueWithoutAi}
                >
                  {t('inventory.wizard.continueWithoutAi')}
                </AppButton>
                {!isMultiLocationWizard ? (
                  <div className="mt-1 flex flex-col gap-3 border-t border-gray-200/80 pt-4 dark:border-gray-700">
                    {renderZeroPhotoWizardLeaveRow()}
                  </div>
                ) : null}
              </>
            ) : (
              <AppButton
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={submitting || bulkAnalyzing || isChainBlocked}
              >
                {submitting ? (
                  t('task.complete.submitting')
                ) : (
                  <>
                    {isMultiLocationWizard
                      ? t('inventory.wizard.saveAndNextLocation')
                      : t('inventory.wizard.continueToFinalReview')}
                    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </AppButton>
            )}
          </div>
        ) : null}
        {helperScreen === 'final' ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <AppButton
              type="button"
              variant="secondary"
              size="lg"
              onClick={handleBackFromWizard}
              disabled={submitting || bulkAnalyzing || isChainBlocked}
              className="flex-1"
            >
              {t('task.action.back')}
            </AppButton>
            <AppButton
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting || bulkAnalyzing || isChainBlocked}
              className="flex-[2]"
            >
              {submitting ? (
                t('task.complete.submitting')
              ) : (
                <>
                  {isResubmit ? t('task.resubmit.submit') : t('inventory.wizard.sendReport')}
                  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </>
              )}
            </AppButton>
          </div>
        ) : null}
      </form>
      <HomeStockLocationPreviewModal
        open={finalReviewLocationPreview !== null}
        locationName={resolveWizardLocationLabel(finalReviewLocationPreviewData?.locationName ?? '')}
        items={finalReviewLocationPreviewData?.items ?? []}
        emptyCategories={finalReviewLocationPreviewData?.emptyCategories ?? []}
        categoryBadgeIndexMap={wizardCategoryBadgeIndexMap}
        resolveCategoryLabel={resolveWizardCategoryLabel}
        todayYmd={finalReviewPreviewTodayYmd}
        taskInstanceId={instanceId}
        onClose={handleCloseFinalReviewLocationPreview}
      />
      <InventorySummaryStatDetailModal
        open={finalReviewStatModalKind !== null}
        title={
          finalReviewStatModalKind === 'expiring'
            ? t('inventory.statDetailModal.expiringTitle', { days: finalReviewStats.expiringWindowDays })
            : t('inventory.statDetailModal.lowStockTitle')
        }
        categoryLabel={t('inventory.statDetailModal.categoryLabel')}
        locationLabel={t('inventory.statDetailModal.locationLabel')}
        emptyMessage={t('inventory.statDetailModal.empty')}
        expiryHeading={
          finalReviewStatModalKind === 'expiring' ? t('inventory.statDetailModal.expiryHeading') : undefined
        }
        rows={
          finalReviewStatModalKind === 'lowStock'
            ? finalReviewStats.lowStockRows.map((r, i) => ({
                rowKey: `helper-low-${i}`,
                itemName: r.itemName,
                categoryName: resolveWizardCategoryLabel(r.categoryName),
                locationName: resolveWizardLocationLabel(r.locationName),
                stockLevel: r.stockLevel,
                stockLevelLabel: inventoryStockLevelUiLabel(r.stockLevel, t),
              }))
            : finalReviewStatModalKind === 'expiring'
              ? finalReviewStats.expiringRows.map((r, i) => ({
                  rowKey: `helper-exp-${i}`,
                  itemName: r.itemName,
                  categoryName: resolveWizardCategoryLabel(r.categoryName),
                  locationName: resolveWizardLocationLabel(r.locationName),
                  detailLine: r.detailLine,
                }))
              : []
        }
        closeAriaLabel={t('inventory.statDetailModal.closeAria')}
        backdropDismissAriaLabel={t('inventory.statDetailModal.dismissBackdropAria')}
        onClose={handleCloseFinalReviewStatModal}
      />
    </div>
  );
};
